import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
        Loader2,
        Plus,
        Sparkles,
        X,
        Download,
        Search,
        FileText,
        Bot,
        TrendingUp,
        Clock,
        FileSearch,
        AlertOctagon,
        Bell,
        CheckCircle2,
        CircleDashed,
        Copy,
        ExternalLink,
        Globe,
        LogOut,
        Menu,
        Settings,
        Image as ImageIcon,
        Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
        api,
        adminHeaders,
        API,
} from "@/lib/api";
import { LLMSelector } from "@/components/LLMSelector";
import { AdminOperationsPanel } from "@/components/admin/AdminOperationsPanel";
import { AutoDiscoverMonitor } from "@/components/admin/AutoDiscoverMonitor";
import { VerifiedJobBanner } from "@/components/admin/VerifiedJobBanner";
import { VerifiedQuotaStatus } from "@/components/admin/VerifiedQuotaStatus";
import { ContextualRiskReview } from "@/components/admin/ContextualRiskReview";
import { DeepResearchPanel } from "@/components/admin/DeepResearchPanel";
import { EditorialQueue } from "@/components/admin/EditorialQueue";
import { LegalRiskPanel } from "@/components/admin/LegalRiskPanel";
import { ADMIN } from "@/constants/testIds";
import {
        CATEGORIES,
        EDITORIAL_STATUSES,
        RELEVANCE_SCOPES,
        emptyForm,
} from "@/lib/adminConfig";
import {
        activeWarningDetails,
        editorialStatusLabel,
        qualityWarningDetails,
        statusReasonDetails,
        technicalWarningDetails,
} from "@/lib/editorialWarnings";
import { sourceDisplayOutlet } from "@/lib/sourceLabels";

// Modelos por defeito de cada papel no OpenCode Go (espelha DEFAULT_ROLE_MODELS
// do backend, para a UI mostrar os corretos mesmo antes de o backend responder).
const OPENCODE_ROLE_DEFAULTS = {
        write: "qwen3.7-max",
        reason: "glm-5.2",
        summarize: "kimi-k2.6",
};

const ADMIN_TABS = [
        { key: "operation", label: "Operação", icon: TrendingUp },
        { key: "generate", label: "Gerar", icon: FileSearch },
        { key: "inbox", label: "Inbox", icon: Clock },
        { key: "queue", label: "Fila", icon: Search },
        { key: "risk", label: "Risco", icon: AlertOctagon },
        { key: "editor", label: "Editor", icon: Sparkles },
        { key: "settings", label: "Configurações", icon: Settings },
];

const EDITOR_WORKSPACE_SECTIONS = [
        {
                id: "editor-section-base",
                label: "Base",
                description: "Título, resumo e publicação",
                icon: FileText,
        },
        {
                id: "editor-section-evidence",
                label: "Evidência",
                description: "Factos e narrativas",
                icon: FileSearch,
        },
        {
                id: "editor-section-text",
                label: "Texto",
                description: "Análise e artigo",
                icon: Sparkles,
        },
        {
                id: "editor-section-review",
                label: "Revisão",
                description: "Risco, fontes e provas",
                icon: AlertOctagon,
        },
        {
                id: "editor-section-publish",
                label: "Guardar",
                description: "Decisão final",
                icon: CheckCircle2,
        },
];

const ADMIN_TOPIC_PAGE_SIZE = 500;
const ADMIN_TOPIC_LIST_ENDPOINT = "/admin/topics/summary/page";

const apiErrorMessage = (error, fallback) => {
        const detail = error?.response?.data?.detail;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
                return detail
                        .map((item) => item?.msg || item?.message || JSON.stringify(item))
                        .join(" · ");
        }
        if (detail && typeof detail === "object") {
                return detail.message || detail.error || JSON.stringify(detail);
        }
        return fallback;
};

const StringList = ({ label, items, onChange, testId, placeholder }) => {
        const [val, setVal] = useState("");
        return (
                <div>
                        <label className="overline block mb-2 text-[var(--ink-soft)]">{label}</label>
                        <div className="space-y-2 mb-3">
                                {items.map((it, i) => (
                                        <div
                                                key={it}
                                                className="flex items-start gap-2 border border-[var(--line)] p-3 bg-[var(--paper)]"
                                        >
                                                <span className="line-no mt-1">
                                                        {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <p className="text-sm flex-1 text-[var(--ink)]">{it}</p>
                                                <button
                                                        type="button"
                                                        onClick={() =>
                                                                onChange(items.filter((_, j) => j !== i))
                                                        }
                                                        className="text-[var(--ink-soft)] hover:text-[var(--deception)]"
                                                >
                                                        <X size={14} strokeWidth={1.5} />
                                                </button>
                                        </div>
                                ))}
                        </div>
                        <div className="flex gap-2">
                                <input
                                        data-testid={testId}
                                        value={val}
                                        onChange={(e) => setVal(e.target.value)}
                                        placeholder={placeholder}
                                        className="flex-1 border bg-transparent px-3 py-2 text-sm"
                                        style={{ borderColor: "var(--line-strong)" }}
                                        onKeyDown={(e) => {
                                                if (e.key === "Enter" && val.trim()) {
                                                        e.preventDefault();
                                                        if (!items.includes(val.trim())) onChange([...items, val.trim()]);
                                                        setVal("");
                                                }
                                        }}
                                />
                                <button
                                        type="button"
                                        onClick={() => {
                                                if (val.trim()) {
                                                        if (!items.includes(val.trim())) onChange([...items, val.trim()]);
                                                        setVal("");
                                                }
                                        }}
                                        className="overline px-3 border hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] transition-colors"
                                        style={{ borderColor: "var(--line-strong)" }}
                                >
                                        Adicionar
                                </button>
                        </div>
                </div>
        );
};

const Modal = ({ open, title, onClose, children, maxWidth = "max-w-lg" }) => {
        useEffect(() => {
                if (!open) return undefined;
                const onKey = (e) => {
                        if (e.key === "Escape") onClose?.();
                };
                document.addEventListener("keydown", onKey);
                return () => document.removeEventListener("keydown", onKey);
        }, [open, onClose]);
        if (!open) return null;
        return (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <button
                                type="button"
                                aria-label="Fechar"
                                onClick={onClose}
                                className="absolute inset-0 bg-black/40"
                        />
                        <div className={`adm-card relative w-full ${maxWidth} max-h-[85vh] overflow-y-auto p-5`}>
                                <div className="mb-4 flex items-center justify-between">
                                        <h3 className="text-base font-semibold text-[var(--ink)]">{title}</h3>
                                        <button type="button" onClick={onClose} className="adm-btn adm-btn-ghost px-2" aria-label="Fechar">
                                                <X size={18} strokeWidth={1.75} />
                                        </button>
                                </div>
                                {children}
                        </div>
                </div>
        );
};

const emptySourceDraft = { title: "", url: "", outlet: "" };

const SourceList = ({ items, onChange }) => {
        const [modalOpen, setModalOpen] = useState(false);
        const [draft, setDraft] = useState(emptySourceDraft);
        const [editIndex, setEditIndex] = useState(null);

        const openAdd = () => {
                setDraft(emptySourceDraft);
                setEditIndex(null);
                setModalOpen(true);
        };
        const openEdit = (i) => {
                const s = items[i] || emptySourceDraft;
                setDraft({ title: s.title || "", url: s.url || "", outlet: s.outlet || "" });
                setEditIndex(i);
                setModalOpen(true);
        };
        const saveDraft = () => {
                if (!draft.title.trim() || !draft.url.trim()) return;
                const entry = { title: draft.title.trim(), url: draft.url.trim(), outlet: draft.outlet.trim() };
                if (editIndex === null) {
                        onChange([...items, entry]);
                } else {
                        onChange(items.map((s, j) => (j === editIndex ? entry : s)));
                }
                setModalOpen(false);
        };

        return (
                <div>
                        <div className="mb-2 flex items-center justify-between">
                                <label className="overline text-[var(--ink-soft)]">Fontes ({items.length})</label>
                                <button type="button" onClick={openAdd} className="adm-btn adm-btn-outline">
                                        <Plus size={14} strokeWidth={1.75} /> Adicionar fonte
                                </button>
                        </div>
                        <div className="space-y-2">
                                {items.map((s, i) => (
                                        <div
                                                key={s.url || i}
                                                className="flex items-start gap-2 rounded-lg border border-[var(--line)] bg-[var(--paper)] p-3"
                                        >
                                                <div className="min-w-0 flex-1">
                                                        <div className="overline text-[var(--ai)]">
                                                                {sourceDisplayOutlet(s)}
                                                        </div>
                                                        <p className="font-serif text-base leading-tight text-[var(--ink)]">
                                                                {s.title}
                                                        </p>
                                                        <a
                                                                href={s.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs font-mono text-[var(--ink-soft)] underline break-all"
                                                        >
                                                                {s.url}
                                                        </a>
                                                </div>
                                                <button
                                                        type="button"
                                                        onClick={() => openEdit(i)}
                                                        className="text-[var(--ink-soft)] hover:text-[var(--controversy)]"
                                                        aria-label="Editar/substituir fonte"
                                                >
                                                        <Pencil size={14} strokeWidth={1.75} />
                                                </button>
                                                <button
                                                        type="button"
                                                        onClick={() => onChange(items.filter((_, j) => j !== i))}
                                                        className="text-[var(--ink-soft)] hover:text-[var(--deception)]"
                                                        aria-label="Remover fonte"
                                                >
                                                        <X size={14} strokeWidth={1.5} />
                                                </button>
                                        </div>
                                ))}
                                {items.length === 0 && (
                                        <p className="rounded-lg border border-dashed border-[var(--line)] p-4 text-sm text-[var(--ink-faint)]">
                                                Sem fontes. Usa "Adicionar fonte" para anexar a prova ou o contexto omitido.
                                        </p>
                                )}
                        </div>

                        <Modal
                                open={modalOpen}
                                onClose={() => setModalOpen(false)}
                                title={editIndex === null ? "Adicionar fonte" : "Substituir fonte"}
                        >
                                <div className="space-y-3">
                                        <div>
                                                <label className="overline mb-1 block text-[var(--ink-soft)]">Título</label>
                                                <input
                                                        autoFocus
                                                        value={draft.title}
                                                        onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                                                        className="w-full border bg-transparent px-3 py-2 text-sm"
                                                        style={{ borderColor: "var(--line-strong)" }}
                                                        placeholder="Título da fonte"
                                                />
                                        </div>
                                        <div>
                                                <label className="overline mb-1 block text-[var(--ink-soft)]">URL</label>
                                                <input
                                                        value={draft.url}
                                                        onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                                                        className="w-full border bg-transparent px-3 py-2 font-mono text-sm"
                                                        style={{ borderColor: "var(--line-strong)" }}
                                                        placeholder="https://..."
                                                />
                                        </div>
                                        <div>
                                                <label className="overline mb-1 block text-[var(--ink-soft)]">Órgão / publicação (opcional)</label>
                                                <input
                                                        value={draft.outlet}
                                                        onChange={(e) => setDraft((d) => ({ ...d, outlet: e.target.value }))}
                                                        className="w-full border bg-transparent px-3 py-2 text-sm"
                                                        style={{ borderColor: "var(--line-strong)" }}
                                                        placeholder="Deixa vazio para derivar do URL"
                                                />
                                        </div>
                                        <div className="flex items-center gap-2 pt-1">
                                                <button
                                                        type="button"
                                                        onClick={saveDraft}
                                                        disabled={!draft.title.trim() || !draft.url.trim()}
                                                        className="adm-btn adm-btn-primary"
                                                >
                                                        {editIndex === null ? "Adicionar" : "Substituir"}
                                                </button>
                                                <button type="button" onClick={() => setModalOpen(false)} className="adm-btn adm-btn-outline">
                                                        Cancelar
                                                </button>
                                        </div>
                                </div>
                        </Modal>
                </div>
        );
};

const FactList = ({ items, onChange }) => {
        const [d, setD] = useState("");
        const [c, setC] = useState("");
        const [t, setT] = useState("");
        return (
                <div>
                        <label className="overline block mb-2 text-[var(--ink-soft)]">Factos Verificados</label>
                        <div className="space-y-2 mb-3">
                                {items.map((f, i) => (
                                        <div
                                                key={i}
                                                className="border border-[var(--line)] p-3 bg-[var(--paper)] flex items-start gap-2"
                                        >
                                                <div className="flex-1 space-y-1">
                                                        <div className="text-xs text-[var(--ink-soft)] font-mono">{f.date}</div>
                                                        <div className="text-sm text-[var(--deception)]">Narrativa: {f.media_claim}</div>
                                                        <div className="text-sm text-[var(--truth)]">Verdade: {f.truth}</div>
                                                </div>
                                                <button
                                                        type="button"
                                                        onClick={() => onChange(items.filter((_, j) => j !== i))}
                                                        className="text-[var(--ink-soft)] hover:text-[var(--deception)]"
                                                >
                                                        <X size={14} strokeWidth={1.5} />
                                                </button>
                                        </div>
                                ))}
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                                <input
                                        placeholder="Data (Ano/Mês)"
                                        value={d}
                                        onChange={(e) => setD(e.target.value)}
                                        className="border bg-transparent px-3 py-2 text-sm"
                                        style={{ borderColor: "var(--line-strong)" }}
                                />
                                <input
                                        placeholder="Narrativa dos media"
                                        value={c}
                                        onChange={(e) => setC(e.target.value)}
                                        className="border bg-transparent px-3 py-2 text-sm"
                                        style={{ borderColor: "var(--line-strong)" }}
                                />
                                <div className="flex gap-2">
                                        <input
                                                placeholder="Facto verdadeiro"
                                                value={t}
                                                onChange={(e) => setT(e.target.value)}
                                                className="flex-1 border bg-transparent px-3 py-2 text-sm"
                                                style={{ borderColor: "var(--line-strong)" }}
                                        />
                                        <button
                                                type="button"
                                                onClick={() => {
                                                        if (d && c && t) {
                                                                onChange([...items, { date: d, media_claim: c, truth: t, source_idx: 1 }]);
                                                                setD(""); setC(""); setT("");
                                                        }
                                                }}
                                                className="overline px-3 py-2 border hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] transition-colors"
                                                style={{ borderColor: "var(--line-strong)" }}
                                        >
                                                Adicionar
                                        </button>
                                </div>
                        </div>
                </div>
        );
};

const ResearchEvidencePanel = ({ evidence, reusedWebsets, webset }) => {
        const hasEvidence = Array.isArray(evidence) && evidence.length > 0;
        const hasReuse = Array.isArray(reusedWebsets) && reusedWebsets.length > 0;
        if (!hasEvidence && !webset && !hasReuse) return null;

        const copyUrl = async (url) => {
                try {
                        await navigator.clipboard.writeText(url);
                        toast.success("URL copiado.");
                } catch {
                        toast.error("Não foi possível copiar o URL.");
                }
        };

        return (
                <section className="adm-card p-5">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                                <div>
                                        <div className="overline text-[var(--ai)] mb-2">Dossier de evidência</div>
                                        <p className="font-serif text-xl leading-tight text-[var(--ink)]">
                                                Fontes extraídas na investigação
                                        </p>
                                </div>
                                {webset && (
                                        <div className="border border-[var(--line)] px-3 py-2 font-mono text-[11px] text-[var(--ink-soft)]">
                                                Webset {webset.id}
                                        </div>
                                )}
                        </div>

                        {hasEvidence ? (
                                <div className="space-y-3">
                                        {evidence.map((item, index) => (
                                                <article key={`${item.url}-${index}`} className="border border-[var(--line)] p-4 bg-[var(--bg-deep)]">
                                                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                                                <div className="min-w-0">
                                                                        <div className="flex flex-wrap gap-2 mb-2">
                                                                                {item.source_idx && (
                                                                                        <span className="overline px-2 py-1 border border-[var(--ai)] text-[var(--ai)]">
                                                                                                Fonte [{item.source_idx}]
                                                                                        </span>
                                                                                )}
                                                                                {item.source_type && (
                                                                                        <span className="overline px-2 py-1 border border-[var(--line)] text-[var(--ink-soft)]">
                                                                                                {item.source_type}
                                                                                        </span>
                                                                                )}
                                                                                {item.published_at && (
                                                                                        <span className="font-mono text-[11px] text-[var(--ink-faint)] py-1">
                                                                                                {item.published_at}
                                                                                        </span>
                                                                                )}
                                                                        </div>
                                                                        <p className="font-serif text-lg leading-tight text-[var(--ink)]">
                                                                                {item.title || "Fonte extraída"}
                                                                        </p>
                                                                        <p className="text-xs font-mono text-[var(--ink-soft)] mt-1 break-all">
                                                                                {item.url}
                                                                        </p>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                        <button
                                                                                type="button"
                                                                                onClick={() => copyUrl(item.url)}
                                                                                className="min-h-9 min-w-9 inline-flex items-center justify-center border border-[var(--line)] hover:border-[var(--ai)] hover:text-[var(--ai)]"
                                                                                title="Copiar URL"
                                                                        >
                                                                                <Copy size={14} strokeWidth={1.5} />
                                                                        </button>
                                                                        <a
                                                                                href={item.url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="min-h-9 min-w-9 inline-flex items-center justify-center border border-[var(--line)] hover:border-[var(--ai)] hover:text-[var(--ai)]"
                                                                                title="Abrir fonte"
                                                                        >
                                                                                <ExternalLink size={14} strokeWidth={1.5} />
                                                                        </a>
                                                                </div>
                                                        </div>

                                                        {item.documented_facts?.length > 0 && (
                                                                <div className="mt-4">
                                                                        <div className="overline text-[var(--truth)] mb-2">Factos documentados</div>
                                                                        <ul className="space-y-1">
                                                                                {item.documented_facts.map((fact) => (
                                                                                        <li key={fact} className="text-sm text-[var(--ink)]">
                                                                                                {fact}
                                                                                        </li>
                                                                                ))}
                                                                        </ul>
                                                                </div>
                                                        )}

                                                        {item.key_quotes?.length > 0 && (
                                                                <div className="mt-4">
                                                                        <div className="overline text-[var(--ai)] mb-2">Citações-chave</div>
                                                                        <ul className="space-y-1">
                                                                                {item.key_quotes.map((quote) => (
                                                                                        <li key={quote} className="text-sm text-[var(--ink-soft)] font-serif">
                                                                                                “{quote}”
                                                                                        </li>
                                                                                ))}
                                                                        </ul>
                                                                </div>
                                                        )}

                                                        {Array.isArray(item.validation_warnings) &&
                                                                item.validation_warnings.includes("llm_extraction_inconclusive") &&
                                                                !(item.documented_facts?.length > 0) && (
                                                                <p className="mt-4 border-t border-[var(--line)] pt-3 text-sm text-[var(--ink-soft)]">
                                                                        Fonte lida, mas sem o que o dossiê procura: a IA não encontrou nesta página factos ou citações sobre o tema. Não é uma falha técnica.
                                                                </p>
                                                        )}

                                                        {(item.unsupported_claims?.length > 0 || item.source_limits) && (
                                                                <div className="mt-4 border-t border-[var(--line)] pt-3">
                                                                        <div className="overline text-[var(--controversy)] mb-2">Limites</div>
                                                                        {item.unsupported_claims?.map((claim) => (
                                                                                <p key={claim} className="text-sm text-[var(--ink-soft)] mb-1">
                                                                                        {claim}
                                                                                </p>
                                                                        ))}
                                                                        {item.source_limits && (
                                                                                <p className="text-sm text-[var(--ink-soft)]">
                                                                                        {item.source_limits}
                                                                                </p>
                                                                        )}
                                                                </div>
                                                        )}
                                                </article>
                                        ))}
                                </div>
                        ) : (
                                <div className="border border-dashed border-[var(--line)] p-4 text-xs font-mono text-[var(--ink-faint)]">
                                        Investigação guardada sem extrações estruturadas.
                                </div>
                        )}

                        {hasReuse && (
                                <div className="mt-4 border-t border-[var(--line-strong)] pt-4">
                                        <div className="overline text-[var(--ink-soft)] mb-2">Memória reutilizada</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                {reusedWebsets.map((item) => (
                                                        <div key={item.id} className="border border-[var(--line)] p-3 font-mono text-xs text-[var(--ink-soft)]">
                                                                <div className="text-[var(--ink)] mb-1">{item.research_query || "investigação anterior"}</div>
                                                                <div>{item.sources || 0} fontes · {item.evidence || 0} extrações</div>
                                                        </div>
                                                ))}
                                        </div>
                                </div>
                        )}
                </section>
        );
};

const WARNING_TONE_CLASS = {
        repair: "border-[var(--truth)] text-[var(--truth)]",
        risk: "border-[var(--deception)] text-[var(--deception)]",
        review: "border-[var(--ai)] text-[var(--ai)]",
        neutral: "border-[var(--line-strong)] text-[var(--ink-soft)]",
};

const QualityWarningsPanel = ({
        contextRiskReview,
        form,
}) => {
        const activeWarnings = activeWarningDetails(form.quality_warnings, { contextRiskReview });
        const technicalNotes = technicalWarningDetails(form.quality_warnings, { contextRiskReview });
        const statusReasons = statusReasonDetails(form);
        const contextRiskCount = contextRiskReview?.findings?.length || 0;

        return (
                <section className="adm-card p-5">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                <div>
                                        <div className="overline text-[var(--ai)] mb-2">
                                                Motivo do estado
                                        </div>
                                        <p className="font-serif text-xl leading-tight text-[var(--ink)]">
                                                {editorialStatusLabel(form.editorial_status)}
                                        </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                        <span className="border border-[var(--line)] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
                                                {activeWarnings.length} ativo{activeWarnings.length === 1 ? "" : "s"} · {technicalNotes.length} nota{technicalNotes.length === 1 ? "" : "s"} · {contextRiskCount} risco{contextRiskCount === 1 ? "" : "s"}
                                        </span>
                                </div>
                        </div>

                        {statusReasons.length > 0 && (
                                <div className="mt-4 space-y-2">
                                        {statusReasons.map((reason, index) => (
                                                <div
                                                        key={`${reason.title}-${index}`}
                                                        className="border border-[var(--line)] bg-[var(--bg-deep)] p-3"
                                                >
                                                        <div className="text-sm text-[var(--ink)]">
                                                                {reason.title}
                                                        </div>
                                                        <p className="mt-1 text-xs leading-relaxed text-[var(--ink-soft)]">
                                                                {reason.detail}
                                                        </p>
                                                </div>
                                        ))}
                                </div>
                        )}

                        {activeWarnings.length > 0 ? (
                                <div className="mt-5 space-y-3">
                                        <div className="overline text-[var(--ink-faint)]">
                                                Avisos ativos
                                        </div>
                                        {activeWarnings.map((warning, index) => (
                                                <article
                                                        key={`${warning.raw}-${index}`}
                                                        className="border border-[var(--line)] bg-[var(--bg-deep)] p-4"
                                                >
                                                        <div className="flex flex-wrap items-center gap-2">
                                                                <span
                                                                        className={`border px-2 py-1 text-[10px] uppercase tracking-[0.14em] ${
                                                                                WARNING_TONE_CLASS[warning.tone] || WARNING_TONE_CLASS.review
                                                                        }`}
                                                                >
                                                                        {warning.tone === "repair" ? "Repair" : warning.tone === "risk" ? "Risco" : "Revisão"}
                                                                </span>
                                                                <h4 className="text-sm text-[var(--ink)]">
                                                                        {warning.title}
                                                                </h4>
                                                        </div>
                                                        <p className="mt-2 text-xs leading-relaxed text-[var(--ink-soft)]">
                                                                {warning.detail}
                                                        </p>
                                                        <code className="mt-3 block break-words border-t border-[var(--line)] pt-3 text-[11px] text-[var(--ink-faint)]">
                                                                {warning.raw}
                                                        </code>
                                                </article>
                                        ))}
                                </div>
                        ) : (
                                <p className="mt-4 border border-dashed border-[var(--line)] p-4 text-xs text-[var(--ink-faint)]">
                                        Este dossiê não tem avisos ativos.
                                </p>
                        )}
                        {technicalNotes.length > 0 && (
                                <details className="mt-4 border border-[var(--line)] bg-[var(--bg-deep)] p-4">
                                        <summary className="cursor-pointer text-xs uppercase tracking-[0.14em] text-[var(--ink-soft)]">
                                                Histórico e notas técnicas ({technicalNotes.length})
                                        </summary>
                                        <div className="mt-4 space-y-3">
                                                {technicalNotes.map((warning, index) => (
                                                        <article
                                                                key={`${warning.raw}-${index}`}
                                                                className="border border-[var(--line)] p-3"
                                                        >
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                        <span
                                                                                className={`border px-2 py-1 text-[10px] uppercase tracking-[0.14em] ${
                                                                                        WARNING_TONE_CLASS[warning.tone] || WARNING_TONE_CLASS.neutral
                                                                                }`}
                                                                        >
                                                                                {warning.tone === "repair" ? "Resolvido" : "Nota"}
                                                                        </span>
                                                                        <h4 className="text-sm text-[var(--ink)]">
                                                                                {warning.title}
                                                                        </h4>
                                                                </div>
                                                                <p className="mt-2 text-xs leading-relaxed text-[var(--ink-soft)]">
                                                                        {warning.detail}
                                                                </p>
                                                                <code className="mt-3 block break-words border-t border-[var(--line)] pt-3 text-[11px] text-[var(--ink-faint)]">
                                                                        {warning.raw}
                                                                </code>
                                                        </article>
                                                ))}
                                        </div>
                                </details>
                        )}
                </section>
        );
};

const wordCount = (value) =>
        String(value || "")
                .trim()
                .split(/\s+/)
                .filter(Boolean).length;

const hasInlineCitation = (value) => /\[[1-9]\d*\]/.test(String(value || ""));

const EditorialReadinessPanel = ({ contextRiskReview, form, researchEvidence }) => {
        const titleWords = wordCount(form.title);
        const articleWords = wordCount(form.full_article);
        const citationsText = [
                form.full_article,
                form.ai_analysis,
                form.deception_strategy,
        ].join("\n");
        const hasRedactions = /\[REDACT:\s*.+?\]/.test(citationsText);
        const pendingRisks = contextRiskReview?.findings?.length || 0;
        const reviewDone = Boolean(contextRiskReview);
        const publishedBlocked =
                form.editorial_status === "published" &&
                ["externo_relevante", "manual_editorial"].includes(form.relevance_scope) &&
                !String(form.portugal_relevance || "").trim();
        const checks = [
                {
                        label: "Título curto",
                        detail: form.title?.trim()
                                ? `${titleWords} palavras`
                                : "em falta",
                        done: Boolean(form.title?.trim()) && titleWords <= 12,
                },
                {
                        label: "Resumo",
                        detail: form.summary?.trim()
                                ? `${wordCount(form.summary)} palavras`
                                : "em falta",
                        done: wordCount(form.summary) >= 20,
                },
                {
                        label: "Artigo completo",
                        detail: `${articleWords} palavras`,
                        done: articleWords >= 320,
                },
                {
                        label: "Factos documentados",
                        detail: `${form.verified_facts?.length || 0} registados`,
                        done: (form.verified_facts?.length || 0) >= 2,
                },
                {
                        label: "Fontes",
                        detail: `${form.sources?.length || 0} anexadas`,
                        done: (form.sources?.length || 0) >= 2,
                },
                {
                        label: "Citações inline",
                        detail: hasInlineCitation(citationsText) ? "presentes" : "sem [1], [2]",
                        done: hasInlineCitation(citationsText) && (form.sources?.length || 0) > 0,
                },
                {
                        label: "Marcações confidenciais",
                        detail: hasRedactions ? "[REDACT: ...] presente" : "sem destaques",
                        done: hasRedactions,
                },
                {
                        label: "Evidência extraída",
                        detail: `${researchEvidence?.length || 0} fonte(s) estruturada(s)`,
                        done: (researchEvidence?.length || 0) > 0 || (form.sources?.length || 0) >= 3,
                },
                {
                        label: "Revisão contextual",
                        detail: reviewDone
                                ? pendingRisks > 0
                                        ? `${pendingRisks} ponto(s) por decidir`
                                        : "sem pontos pendentes"
                                : "por correr",
                        done: reviewDone && pendingRisks === 0,
                },
                {
                        label: "Publicação",
                        detail: publishedBlocked
                                ? "falta ligação a Portugal"
                                : form.editorial_status || "rascunho",
                        done: !publishedBlocked,
                },
        ];
        const completed = checks.filter((check) => check.done).length;
        const readyTone =
                completed === checks.length
                        ? "var(--truth)"
                        : completed >= Math.ceil(checks.length * 0.7)
                        ? "var(--controversy)"
                        : "var(--ink-soft)";

        return (
                <div className="adm-card p-5 relative">
                        <div className="absolute -top-3 left-5 stamp-ai stamp">
                                Prontidão editorial
                        </div>
                        <div className="mt-3 mb-5">
                                <div className="overline text-[var(--ink-faint)] mb-2">
                                        Estado do dossiê
                                </div>
                                <div className="font-serif text-4xl leading-none" style={{ color: readyTone }}>
                                        {completed}/{checks.length}
                                </div>
                        </div>
                        <div className="space-y-3">
                                {checks.map((check) => {
                                        const Icon = check.done ? CheckCircle2 : CircleDashed;
                                        return (
                                                <div
                                                        key={check.label}
                                                        className="grid grid-cols-[18px_1fr] gap-3 border-b border-[var(--line)] last:border-b-0 pb-3 last:pb-0"
                                                >
                                                        <Icon
                                                                size={16}
                                                                strokeWidth={1.6}
                                                                className={check.done ? "text-[var(--truth)]" : "text-[var(--ink-faint)]"}
                                                        />
                                                        <div>
                                                                <div className="text-sm text-[var(--ink)]">
                                                                        {check.label}
                                                                </div>
                                                                <div className="font-mono text-[11px] text-[var(--ink-soft)]">
                                                                        {check.detail}
                                                                </div>
                                                        </div>
                                                </div>
                                        );
                                })}
                        </div>
                </div>
        );
};

const formatReviewSavedAt = (value) => {
        if (!value) return "ainda não guardado";
        try {
                return new Date(value).toLocaleString("pt-PT", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                });
        } catch {
                return "guardado";
        }
};

const EditorInspectorPanel = ({
        contextRiskReview,
        form,
        researchEvidence,
        reviewStateSavedAt,
}) => {
        const pendingRisks = contextRiskReview?.findings?.length || 0;
        const decisions = contextRiskReview?.decisions?.length || 0;
        const cleared = contextRiskReview?.cleared_excerpts?.length || 0;
        const warningDetails = qualityWarningDetails(form.quality_warnings, { contextRiskReview });
        const activeWarnings = activeWarningDetails(form.quality_warnings, { contextRiskReview });
        const technicalNotes = technicalWarningDetails(form.quality_warnings, { contextRiskReview });
        const riskWarnings = warningDetails.filter((warning) => warning.tone === "risk").length;
        const repairWarnings = warningDetails.filter((warning) => warning.tone === "repair").length;
        return (
                <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
                        <EditorialReadinessPanel
                                contextRiskReview={contextRiskReview}
                                form={form}
                                researchEvidence={researchEvidence}
                        />
                        <div className="adm-card p-5">
                                <div className="overline text-[var(--ink-faint)] mb-4">
                                        Mesa de trabalho
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                        {[
                                                ["Fontes", form.sources?.length || 0],
                                                ["Factos", form.verified_facts?.length || 0],
                                                ["Riscos contextuais", pendingRisks],
                                                ["Avisos ativos", activeWarnings.length],
                                                ["Avisos de risco", riskWarnings],
                                                ["Decisões", decisions],
                                        ].map(([label, value]) => (
                                                <div key={label} className="border border-[var(--line)] bg-[var(--bg-deep)] p-3">
                                                        <div className="overline text-[var(--ink-faint)] mb-1">
                                                                {label}
                                                        </div>
                                                        <div className="font-serif text-2xl text-[var(--ink)]">
                                                                {value}
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                                <div className="mt-4 border-t border-[var(--line)] pt-4 space-y-2 text-xs font-mono text-[var(--ink-soft)]">
                                        <p>Revisão aceite no contexto: {cleared}</p>
                                        <p>Estado da revisão: {formatReviewSavedAt(reviewStateSavedAt)}</p>
                                        <p>Estado editorial: {editorialStatusLabel(form.editorial_status)}</p>
                                        <p>Notas técnicas: {technicalNotes.length}</p>
                                        <p>Reparações registadas: {repairWarnings}</p>
                                </div>
                        </div>
                </aside>
        );
};

const EditorWorkspaceNav = ({ sections, active, onSelect, badges = {} }) => (
        <nav
                aria-label="Secções do editor"
                className="flex flex-wrap gap-1 border-b border-[var(--line)]"
        >
                {sections.map((section) => {
                        const Icon = section.icon;
                        const isActive = active === section.id;
                        const badge = badges[section.id];
                        return (
                                <button
                                        key={section.id}
                                        type="button"
                                        onClick={() => onSelect?.(section.id)}
                                        title={section.description}
                                        className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                                isActive
                                                        ? "border-[var(--controversy)] text-[var(--controversy)]"
                                                        : "border-transparent text-[var(--ink-soft)] hover:text-[var(--ink)]"
                                        }`}
                                >
                                        <Icon size={15} strokeWidth={1.75} />
                                        {section.label}
                                        {badge ? (
                                                <span className="adm-badge bg-[rgba(220,38,38,0.12)] text-[var(--deception)]">{badge}</span>
                                        ) : null}
                                </button>
                        );
                })}
        </nav>
);

const EditorSection = ({ children, description, eyebrow, id, title, active = true }) => (
        <section
                id={id}
                className={`scroll-mt-24 adm-card p-5 md:p-6 ${active ? "" : "hidden"}`}
        >
                <div className="mb-6 border-b border-[var(--line)] pb-4">
                        <div className="overline text-[var(--ink-faint)] mb-2">
                                {eyebrow}
                        </div>
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                                <h2 className="font-serif text-2xl md:text-3xl leading-tight text-[var(--ink)]">
                                        {title}
                                </h2>
                                {description && (
                                        <p className="max-w-xl font-mono text-xs leading-relaxed text-[var(--ink-soft)]">
                                                {description}
                                        </p>
                                )}
                        </div>
                </div>
                <div className="space-y-6">{children}</div>
        </section>
);

export default function AdminPanel() {
        const navigate = useNavigate();
        const [topics, setTopics] = useState([]);
        const [tips, setTips] = useState([]);
        const [suggestions, setSuggestions] = useState([]);
        const [editing, setEditing] = useState(null);
        const [form, setForm] = useState(emptyForm());
        const [provider, setProvider] = useState("anthropic");
        const [model, setModel] = useState("");
        const [roleModels, setRoleModels] = useState({ write: "", reason: "", summarize: "" });
        const [roleDefaults, setRoleDefaults] = useState({});
        const [opencodeCatalog, setOpencodeCatalog] = useState([]);
        const [llmProviders, setLlmProviders] = useState([]);
        const [savingLLM, setSavingLLM] = useState(false);
        const [hint, setHint] = useState("");
        const [generating, setGenerating] = useState(false);
        const [saving, setSaving] = useState(false);
        const [notifySubscribers, setNotifySubscribers] = useState(false);
        const [ingesting, setIngesting] = useState(false);
	const [appSettings, setAppSettings] = useState({ auto_discover_enabled: false, auto_discover_interval_minutes: 300, auto_discover_limit: 3, rss_sync_enabled: false, rss_sync_interval_minutes: 60 });
	const [triggeringDiscover, setTriggeringDiscover] = useState(false);
	const [discoverRefresh, setDiscoverRefresh] = useState(0);
	const [savingSettings, setSavingSettings] = useState(false);
        const [researching, setResearching] = useState(false);
        const [researchQuery, setResearchQuery] = useState("");
        // Categoria da investigação por prompt: "" = o Infinitum deduz o tema.
        const [researchCategory, setResearchCategory] = useState("");
        const [researchEvidence, setResearchEvidence] = useState([]);
        const [deepResearch, setDeepResearch] = useState(null);
        const [researchWebset, setResearchWebset] = useState(null);
        const [reusedWebsets, setReusedWebsets] = useState([]);
        const [searchingImages, setSearchingImages] = useState(false);
        const [stats, setStats] = useState(null);
        const [opsStatus, setOpsStatus] = useState(null);
        const [opsJobs, setOpsJobs] = useState([]);
        const [opsUsage, setOpsUsage] = useState(null);
        const [auditLog, setAuditLog] = useState([]);
        const [loadingOps, setLoadingOps] = useState(false);
        const [cleaningUploads, setCleaningUploads] = useState(false);
        const [uploadCleanupResult, setUploadCleanupResult] = useState(null);
        const [topicStatusFilter, setTopicStatusFilter] = useState("all");
        const [topicSearch, setTopicSearch] = useState("");
        const [loadingTopics, setLoadingTopics] = useState(false);
        const [topicPage, setTopicPage] = useState({
                total: 0,
                limit: ADMIN_TOPIC_PAGE_SIZE,
                offset: 0,
                has_more: false,
        });
        const [riskFindings, setRiskFindings] = useState([]);
        const [riskScanLimit, setRiskScanLimit] = useState(30);
        const [riskEditorialStatus, setRiskEditorialStatus] = useState("published");
        const [contextRiskReview, setContextRiskReview] = useState(null);
        const [reviewStateSavedAt, setReviewStateSavedAt] = useState(null);
        const [activeTab, setActiveTab] = useState("operation");
        const [sidebarOpen, setSidebarOpen] = useState(false);
        const [editorSection, setEditorSection] = useState("editor-section-base");
        const [showGenerator, setShowGenerator] = useState(false);
        const [showImageModal, setShowImageModal] = useState(false);
        const editorFieldRefs = useRef({});

        const [generatingTitle, setGeneratingTitle] = useState(false);
        // Título verificado pelo Infinitum (dossiês da escrita verificada).
        const [titleCheck, setTitleCheck] = useState({ loading: null, result: null });
        // Escrita verificada pedida pelo editor: um pedido de cada vez, guardado
        // no browser para sobreviver ao F5 (o estado real vem do Infinitum).
        const [verifiedJob, setVerifiedJobState] = useState(() => {
                try {
                        return JSON.parse(window.localStorage.getItem("vo-verified-job") || "null");
                } catch {
                        return null;
                }
        });
        const [startingVerified, setStartingVerified] = useState(false);
        const [verifiedQuota, setVerifiedQuota] = useState(null);
        const setVerifiedJob = useCallback((next) => {
                setVerifiedJobState(next);
                try {
                        if (next) window.localStorage.setItem("vo-verified-job", JSON.stringify(next));
                        else window.localStorage.removeItem("vo-verified-job");
                } catch {
                        // Sem armazenamento: continua, só não sobrevive ao F5.
                }
        }, []);

        const searchImages = async () => {
                if (!form.title) {
                        toast.error("O dossiê precisa de um título primeiro.");
                        return;
                }
                setSearchingImages(true);
                try {
                        const r = await api.post(
                                "/admin/images/search",
                                { query: form.title },
                                { headers: adminHeaders() }
                        );
                        if (r.data.images && r.data.images.length > 0) {
                                setForm((f) => ({
                                        ...f,
                                        image_suggestions: [...f.image_suggestions, ...r.data.images],
                                }));
                                toast.success(`${r.data.images.length} imagens encontradas.`);
                        } else {
                                toast.info("Nenhuma imagem nova encontrada.");
                        }
                } catch (e) {
                        console.error("Image search failed:", e);
                } finally {
                        setSearchingImages(false);
                }
        };

        useEffect(() => {
                if (!verifiedJob?.job_id || !["pending", "running", "queued", "processing"].includes(verifiedJob.status)) return undefined;
                const timer = window.setTimeout(async () => {
                        try {
                                const { data } = await api.get(`/admin/verified-dossier/${verifiedJob.job_id}`, { headers: adminHeaders() });
                                const next = { ...verifiedJob, status: data.status, error: data.error || "", reason: data.reason || "", progress: data.progress || null };
                                if (data.status === "succeeded") {
                                        next.dossier = data.dossier || null;
                                        next.report = data.report || null;
                                        toast.success(data.dossier ? "Escrita verificada pronta." : "Escrita verificada terminou sem prova suficiente.");
                                }
                                setVerifiedJob(next);
                        } catch (e) {
                                if (e?.response?.status === 404) setVerifiedJob({ ...verifiedJob, status: "failed", error: "O Infinitum já não conhece este pedido." });
                        }
                }, 10000);
                return () => window.clearTimeout(timer);
        }, [verifiedJob, setVerifiedJob]);

        // Estado da quota: ao abrir o painel, de 5 em 5 minutos e quando um
        // pedido termina ou é recusado.
        const verifiedJobStatus = verifiedJob?.status || "";
        useEffect(() => {
                let cancelled = false;
                const load = async () => {
                        try {
                                const { data } = await api.get("/admin/verified-quota", { headers: adminHeaders() });
                                if (!cancelled) setVerifiedQuota(data);
                        } catch {
                                if (!cancelled) setVerifiedQuota(null);
                        }
                };
                load();
                const timer = window.setInterval(load, 300000);
                return () => {
                        cancelled = true;
                        window.clearInterval(timer);
                };
        }, [verifiedJobStatus]);

        const startVerified = async (body, meta = {}) => {
                if (verifiedJob && ["pending", "running", "queued", "processing"].includes(verifiedJob.status)) {
                        toast.error("Já há uma escrita verificada a correr; espera que termine.");
                        return false;
                }
                setStartingVerified(true);
                try {
                        const { data } = await api.post("/admin/verified-dossier", body, { headers: adminHeaders() });
                        setVerifiedJob({
                                job_id: data.job_id,
                                mode: body.mode,
                                title: data.title || body.title || "",
                                // Só a categoria escolhida no pedido; em "Automático" vale a do Infinitum.
                                category: body.mode === "renew" ? (body.category || form.category) : (body.category || ""),
                                topicId: meta.topicId || null,
                                // O próprio pedido, para o botão "Repetir pedido" se falhar.
                                request: { body, meta },
                                // O pedido original fica no dossiê: a renovação usa-o como ângulo.
                                brief: body.mode === "renew" ? (form.evidence_pack?._vo_editor_brief || "") : (body.brief || body.title || ""),
                                status: "pending",
                                startedAt: new Date().toISOString(),
                        });
                        toast.success("Escrita verificada lançada no Infinitum (10–12 min).");
                        return true;
                } catch (e) {
                        const detail = e?.response?.data?.detail || "Não foi possível lançar a escrita verificada.";
                        toast.error(detail);
                        // Aviso fixo (sobrevive ao F5): uma recusa por quota não pode
                        // desaparecer num toast de segundos.
                        setVerifiedJob({
                                job_id: "",
                                request: { body, meta },
                                mode: body.mode,
                                title: body.title || (body.brief || "").slice(0, 120),
                                status: "blocked",
                                error: typeof detail === "string" ? detail : "o Infinitum recusou o pedido.",
                                startedAt: new Date().toISOString(),
                        });
                        return false;
                } finally {
                        setStartingVerified(false);
                }
        };

        const openVerifiedResult = async () => {
                const job = verifiedJob;
                if (!job?.dossier) return;
                if (job.mode === "renew" && job.topicId) {
                        try {
                                const res = await api.get(`/admin/topics/${job.topicId}`, { headers: adminHeaders() });
                                await startEdit(res.data);
                        } catch {
                                toast.error("Não foi possível abrir o dossiê a renovar.");
                                return;
                        }
                } else {
                        startNew();
                        setForm((f) => ({ ...f, category: job.category || job.dossier?.category || f.category }));
                }
                applyAI(job.dossier, "review");
                if (job.brief) {
                        setForm((f) => ({ ...f, evidence_pack: { ...(f.evidence_pack || {}), _vo_editor_brief: job.brief } }));
                }
                setActiveTab("editor");
                setVerifiedJob(null);
                toast.success(job.mode === "renew" ? "Renovação aberta: só substitui o dossiê se guardares." : "Dossiê verificado aberto: revê e guarda.");
        };

        const verifiedJobId = form.evidence_pack?._vo_validation?._vo_verified_article?.job_id || "";
        const validatedTitle = form.evidence_pack?._vo_validated_title;
        const titlePendingValidation = Boolean(verifiedJobId) && typeof validatedTitle === "string" && form.title !== validatedTitle;
        const markTitleValidated = (title) =>
                setForm((f) => ({ ...f, title, evidence_pack: { ...(f.evidence_pack || {}), _vo_validated_title: title } }));
        const runVerifiedTitle = async (mode) => {
                setTitleCheck({ loading: mode, result: null });
                try {
                        const res = await api.post(
                                "/admin/verified-title",
                                { mode, title: mode === "validate" ? form.title : undefined, evidence_pack: form.evidence_pack || {} },
                                { headers: adminHeaders() }
                        );
                        setTitleCheck({ loading: null, result: res.data });
                        if (mode === "validate" && res.data?.validation?.ok) {
                                markTitleValidated(form.title);
                                toast.success("Título validado pelo Infinitum.");
                        } else if (mode === "validate") {
                                toast.info("O título não passou; vê os motivos e as alternativas.");
                        }
                } catch (e) {
                        setTitleCheck({ loading: null, result: null });
                        toast.error(e?.response?.data?.detail || "Não foi possível verificar o título.");
                }
        };

        const searchMoreImages = async () => {
                if (!form.title) return;
                try {
                        setSearchingImages(true);
                        const res = await api.get(`/admin/search-images?q=${encodeURIComponent(form.title)}`, { headers: adminHeaders() });
                        if (res.data && res.data.length > 0) {
                                setForm((f) => {
                                        const current = f.image_suggestions || [];
                                        const combined = [...current, ...res.data];
                                        return { ...f, image_suggestions: [...new Set(combined)].slice(0, 15) };
                                });
                        }
                } catch (e) {
                        console.error("Image search failed:", e);
                } finally {
                        setSearchingImages(false);
                }
        };

        const loadOperations = useCallback(async () => {
                setLoadingOps(true);
                try {
                        const [ops, jobs, usage, audit] = await Promise.all([
                                api.get("/admin/ops/status", { headers: adminHeaders() }),
                                api.get("/admin/ops/jobs", { headers: adminHeaders() }),
                                api.get("/admin/ops/usage", { headers: adminHeaders() }),
                                api.get("/admin/audit", { headers: adminHeaders() }),
                        ]);
                        setOpsStatus(ops.data);
                        setOpsJobs(jobs.data || []);
                        setOpsUsage(usage.data || null);
                        setAuditLog(audit.data || []);
                } catch (e) {
                        if (e.response?.status === 401) {
                                navigate("/admin");
                        }
                } finally {
                        setLoadingOps(false);
                }
        }, [navigate]);

        const cleanupUnusedUploads = async () => {
                setCleaningUploads(true);
                try {
                        const response = await api.post(
                                "/admin/uploads/cleanup",
                                { min_age_hours: 0 },
                                { headers: adminHeaders() }
                        );
                        const data = response.data || {};
                        setUploadCleanupResult(data);
                        toast.success(`${data.deleted || 0} imagem(ns) órfã(s) removida(s).`);
                } catch (error) {
                        toast.error(apiErrorMessage(error, "Não foi possível limpar imagens órfãs."));
                } finally {
                        setCleaningUploads(false);
                }
        };

        const fetchAdminTopics = useCallback(async (query = "") => {
                setLoadingTopics(true);
                try {
                        const params = { limit: ADMIN_TOPIC_PAGE_SIZE, offset: 0 };
                        if (query.trim()) params.search = query.trim();
                        const response = await api.get(ADMIN_TOPIC_LIST_ENDPOINT, { headers: adminHeaders(), params });
                        setTopics(response.data?.items || []);
                        setTopicPage({
                                total: response.data?.total || 0,
                                limit: response.data?.limit || ADMIN_TOPIC_PAGE_SIZE,
                                offset: response.data?.offset || 0,
                                has_more: Boolean(response.data?.has_more),
                        });
                } catch (e) {
                        if (e.response?.status === 401) {
                                navigate("/admin");
                        } else {
                                toast.error("Não foi possível pesquisar dossiês.");
                        }
                } finally {
                        setLoadingTopics(false);
                }
        }, [navigate]);

        const loadMoreAdminTopics = useCallback(async () => {
                if (loadingTopics || !topicPage.has_more) return;
                setLoadingTopics(true);
                try {
                        const params = {
                                limit: ADMIN_TOPIC_PAGE_SIZE,
                                offset: topics.length,
                        };
                        if (topicSearch.trim()) params.search = topicSearch.trim();
                        const response = await api.get(ADMIN_TOPIC_LIST_ENDPOINT, { headers: adminHeaders(), params });
                        const nextTopics = response.data?.items || [];
                        setTopics((current) => [...current, ...nextTopics]);
                        setTopicPage({
                                total: response.data?.total || topics.length + nextTopics.length,
                                limit: response.data?.limit || ADMIN_TOPIC_PAGE_SIZE,
                                offset: response.data?.offset || topics.length,
                                has_more: Boolean(response.data?.has_more),
                        });
                } catch (e) {
                        if (e.response?.status === 401) {
                                navigate("/admin");
                        } else {
                                toast.error("Não foi possível carregar mais dossiês.");
                        }
                } finally {
                        setLoadingTopics(false);
                }
        }, [loadingTopics, navigate, topicPage.has_more, topicSearch, topics.length]);

        const persistContextRiskReviewState = useCallback(async (topicId, reviewState) => {
                if (!topicId || !reviewState) return;
                try {
                        const response = await api.put(
                                `/admin/topics/${topicId}/review-state`,
                                { review_state: reviewState },
                                { headers: adminHeaders() }
                        );
                        setReviewStateSavedAt(response.data?.updated_at || new Date().toISOString());
                } catch (error) {
                        console.error("Failed to persist contextual review state:", error);
                        toast.error("Não foi possível guardar o estado da revisão contextual.");
                }
        }, []);

        const removeResolvedWarningsFromForm = useCallback((reviewState) => {
                const resolvedWarnings = new Set(
                        (reviewState?.warning_reviews || [])
                                .filter((item) => item?.status === "resolved" && item?.warning)
                                .map((item) => String(item.warning))
                );
                if (resolvedWarnings.size === 0) return;

                const existingWarnings = Array.isArray(form.quality_warnings)
                        ? form.quality_warnings
                        : [];
                const removedCount = existingWarnings.filter((warning) =>
                        resolvedWarnings.has(String(warning))
                ).length;
                if (removedCount === 0) return;

                setForm((current) => {
                        const currentWarnings = Array.isArray(current.quality_warnings)
                                ? current.quality_warnings
                                : [];
                        const nextWarnings = currentWarnings.filter(
                                (warning) => !resolvedWarnings.has(String(warning))
                        );
                        if (nextWarnings.length === currentWarnings.length) return current;
                        return { ...current, quality_warnings: nextWarnings };
                });
                toast.success(`${removedCount} aviso(s) resolvido(s) pela revisão contextual.`);
        }, [form.quality_warnings]);

        const removeReviewedSourceFromForm = useCallback((sourceIdx) => {
                const idx = Number(sourceIdx);
                if (!Number.isInteger(idx) || idx < 1) {
                        toast.error("Índice de fonte inválido.");
                        return;
                }

                const remapCitations = (value) =>
                        String(value || "")
                                .replace(/\[(\d{1,2})\]/g, (match, raw) => {
                                        const citation = Number(raw);
                                        if (!Number.isInteger(citation)) return match;
                                        if (citation === idx) return "";
                                        if (citation > idx) return `[${citation - 1}]`;
                                        return match;
                                })
                                .replace(/\s+([,.;!?])/g, "$1")
                                .replace(/[ \t]{2,}/g, " ");

                setForm((current) => {
                        const sources = Array.isArray(current.sources) ? current.sources : [];
                        if (idx > sources.length) return current;
                        const nextSources = sources.filter((_, index) => index !== idx - 1);
                        const nextFacts = (current.verified_facts || []).map((fact) => {
                                if (!fact || typeof fact !== "object") return fact;
                                const rawSourceIdx = Number(fact.source_idx);
                                if (!Number.isInteger(rawSourceIdx)) return fact;
                                if (rawSourceIdx === idx) {
                                        const { source_idx: _removedSourceIdx, ...rest } = fact;
                                        return rest;
                                }
                                if (rawSourceIdx > idx) {
                                        return { ...fact, source_idx: rawSourceIdx - 1 };
                                }
                                return fact;
                        });
                        return {
                                ...current,
                                sources: nextSources,
                                verified_facts: nextFacts,
                                summary: remapCitations(current.summary),
                                deception_strategy: remapCitations(current.deception_strategy),
                                full_article: remapCitations(current.full_article),
                                ai_analysis: remapCitations(current.ai_analysis),
                        };
                });
                toast.success(`Fonte [${idx}] removida do formulário. Revê e guarda quando estiver certo.`);
        }, []);

        const addReviewedSourceToForm = useCallback((source) => {
                const cleanUrl = String(source?.url || "").trim();
                const cleanTitle = String(source?.title || "").trim();
                if (!cleanUrl || !cleanTitle) {
                        toast.error("A fonte sugerida não tem título e URL suficientes.");
                        return;
                }
                const existingSources = Array.isArray(form.sources) ? form.sources : [];
                const alreadyExists = existingSources.some(
                        (item) => String(item?.url || "").trim() === cleanUrl
                );
                if (alreadyExists) {
                        toast.info("Essa fonte já está no formulário.");
                        return;
                }
                setForm((current) => ({
                        ...current,
                        sources: [
                                ...(Array.isArray(current.sources) ? current.sources : []),
                                {
                                        title: cleanTitle,
                                        url: cleanUrl,
                                        outlet: String(source?.outlet || "").trim() || "Infinitum",
                                },
                        ],
                }));
                toast.success("Fonte adicionada ao formulário. Revê e guarda quando estiver certo.");
        }, [form.sources]);

        const persistCurrentContextRiskReview = useCallback((nextReview) => {
                const topicId = editing && editing !== "new" ? editing.id : form.id;
                if (!topicId) return;
                persistContextRiskReviewState(topicId, nextReview);
        }, [editing, form.id, persistContextRiskReviewState]);

        const loadContextRiskReviewState = useCallback(async (topicId) => {
                if (!topicId) return;
                try {
                        const response = await api.get(
                                `/admin/topics/${topicId}/review-state`,
                                { headers: adminHeaders() }
                        );
                        const savedReview = response.data?.review_state || null;
                        setContextRiskReview(savedReview);
                        setReviewStateSavedAt(response.data?.updated_at || null);
                } catch (error) {
                        console.error("Failed to load contextual review state:", error);
                        setContextRiskReview(null);
                        setReviewStateSavedAt(null);
                }
        }, []);

        const reload = useCallback(async () => {
                try {
                        const [t, tp, sugg, s, llm, appSet] = await Promise.all([
                                api.get(ADMIN_TOPIC_LIST_ENDPOINT, { headers: adminHeaders(), params: { limit: ADMIN_TOPIC_PAGE_SIZE, offset: 0 } }),
                                api.get("/admin/tips", { headers: adminHeaders() }),
                                api.get("/admin/suggestions", { headers: adminHeaders() }),
                                api.get("/stats"),
                                api.get("/admin/llm-settings", { headers: adminHeaders() }),
                                api.get("/admin/app-settings", { headers: adminHeaders() }),
                        ]);
                        setTopics(t.data?.items || []);
                        setTopicPage({
                                total: t.data?.total || 0,
                                limit: t.data?.limit || ADMIN_TOPIC_PAGE_SIZE,
                                offset: t.data?.offset || 0,
                                has_more: Boolean(t.data?.has_more),
                        });
                        setTips(tp.data);
                        setSuggestions(sugg.data);
                        setStats(s.data);
                        setProvider(llm.data.provider);
                        setModel(llm.data.model);
                        setRoleModels(llm.data.role_models || { write: "", reason: "", summarize: "" });
                        setRoleDefaults(llm.data.role_defaults || {});
                        setOpencodeCatalog(llm.data.opencode_catalog || []);
                        setLlmProviders(llm.data.providers || []);
                        setAppSettings(appSet.data || { auto_discover_enabled: false, auto_discover_interval_minutes: 300, auto_discover_limit: 3, rss_sync_enabled: false, rss_sync_interval_minutes: 60 });
                        loadOperations();
                } catch (e) {
                        if (e.response?.status === 401) {
                                navigate("/admin");
                        }
                }
        }, [navigate, loadOperations]);

        useEffect(() => {
                reload();
        }, [reload]);

        const startNew = () => {
                setForm(emptyForm());
                setHint("");
                setEditing("new");
                setNotifySubscribers(false);
                setContextRiskReview(null);
                setReviewStateSavedAt(null);
                setResearchEvidence([]);
                setResearchWebset(null);
                setReusedWebsets([]);
                setActiveTab("editor");
                setEditorSection("editor-section-base");
                setShowGenerator(false);
        };

        const startEdit = async (t) => {
                setForm({ ...emptyForm(), ...t });
                setHint("");
                setEditing(t);
                setEditorSection("editor-section-base");
                setShowGenerator(false);
                setNotifySubscribers(false);
                setContextRiskReview(null);
                setReviewStateSavedAt(null);
                // Carrega a evidência estruturada já existente do dossiê. Sem isto,
                // a "Analisar Artigo Completo" enviava research_evidence vazio e a
                // revisão nunca conseguia sugerir as fontes de contexto omitido.
                setResearchEvidence(Array.isArray(t?.research_evidence) ? t.research_evidence : []);
                setResearchWebset(null);
                setReusedWebsets([]);
                setActiveTab("editor");
                if (t?.id) {
                        loadContextRiskReviewState(t.id);
                }
                if (t?.compact && t?.id) {
                        try {
                                const response = await api.get(`/admin/topics/${t.id}`, {
                                        headers: adminHeaders(),
                                });
                                const fullTopic = response.data;
                                setForm({ ...emptyForm(), ...fullTopic });
                                setEditing(fullTopic);
                                if (Array.isArray(fullTopic.research_evidence)) {
                                        setResearchEvidence(fullTopic.research_evidence);
                                }
                        } catch (error) {
                                if (error.response?.status === 401) {
                                        navigate("/admin");
                                } else {
                                        toast.error("Não foi possível carregar o dossiê completo.");
                                }
                        }
                }
        };

        const analyzeTip = async (tip) => {
                setForm(emptyForm());
                setHint(`Denúncia Anónima:\n"${tip.content}"\nFonte: ${tip.source_url || "N/A"}\n\nPor favor analisa esta denúncia, faz uma investigação web se necessário, e escreve um novo dossiê detalhado expondo este esquema.`);
                setResearchQuery("Investigação a Denúncia");
                setEditing("new");
                setNotifySubscribers(false);
                setContextRiskReview(null);
                setReviewStateSavedAt(null);
                setResearchEvidence([]);
                setResearchWebset(null);
                setReusedWebsets([]);
                setActiveTab("editor");
                
                try {
                        await api.post(`/admin/tips/${tip.id}/status`, { status: "analyzed" }, { headers: adminHeaders() });
                        reload();
                } catch(e) {
                        toast.error("Aviso: Não foi possível marcar a denúncia como analisada.");
                }
        };

        const cancel = () => {
                setEditing(null);
                setForm(emptyForm());
                setHint("");
                setNotifySubscribers(false);
                setResearchEvidence([]);
                setResearchWebset(null);
                setReusedWebsets([]);
                setContextRiskReview(null);
                setReviewStateSavedAt(null);
                setActiveTab("queue");
        };

        const searchEditorialTopics = (event) => {
                event?.preventDefault();
                setTopicStatusFilter("all");
                fetchAdminTopics(topicSearch, "all");
        };

        const clearEditorialTopicSearch = () => {
                setTopicSearch("");
                setTopicStatusFilter("all");
                fetchAdminTopics("", "all");
        };

        const addDeepResearchSources = (verified) => {
                const items = Array.isArray(verified) ? verified : [];
                const known = new Set((form.sources || []).map((src) => String(src.url || "").replace(/\/+$/, "")));
                const fresh = items.filter((src) => src.url && !known.has(String(src.url).replace(/\/+$/, "")));
                if (!fresh.length) {
                        toast("Todas as fontes verificadas já estão no dossiê.");
                        return;
                }
                setForm((f) => ({
                        ...f,
                        sources: [...(f.sources || []), ...fresh.map((src) => ({ title: src.title, url: src.url, outlet: src.outlet || "" }))],
                }));
                setResearchEvidence((cards) => [
                        ...(cards || []),
                        ...fresh.map((src) => ({
                                title: src.title,
                                url: src.url,
                                outlet: src.outlet || "",
                                published_at: src.published_at || "",
                                source_type: src.publisher_type || "",
                                documented_facts: src.supported_claims || [],
                                key_quotes: src.usable_quotes || [],
                                extractor: "infinitum_deep_research",
                        })),
                ]);
                toast.success(`${fresh.length} fonte(s) verificada(s) juntas ao dossiê.`);
        };

        const applyAI = (d, fallbackStatus = "draft") => {
                setContextRiskReview(null);
                setReviewStateSavedAt(null);
                if (Array.isArray(d.research_evidence)) {
                        setResearchEvidence(d.research_evidence);
                }
                setForm((f) => ({
                        ...f,
                        title: d.title ?? f.title,
                        editorial_status: d.editorial_status ?? fallbackStatus,
                        summary: d.summary ?? f.summary,
                        controversy_score: d.controversy_score ?? f.controversy_score,
                        tags: d.tags ?? f.tags,
                        verified_facts: d.verified_facts ?? f.verified_facts,
                        media_narratives: d.media_narratives ?? f.media_narratives,
                        deception_strategy: d.deception_strategy ?? f.deception_strategy,
                        full_article: d.full_article ?? f.full_article,
                        ai_analysis: d.ai_analysis ?? f.ai_analysis,
                        ai_model_used: d.ai_model_used ?? f.ai_model_used,
                        quality_warnings: d.quality_warnings ?? f.quality_warnings,
                        sources: d.sources ?? f.sources,
                        image_suggestions: d.image_suggestions ?? f.image_suggestions,
                        // A validação que a geração fez vem carimbada no pack. Antes era
                        // deitada fora aqui, e cada revisão tinha de revalidar do zero.
                        evidence_pack: d.evidence_pack ?? f.evidence_pack,
                }));
        };


        
	const triggerAutoDiscover = async () => {
		setTriggeringDiscover(true);
		try {
			const response = await api.post("/admin/auto-discover/trigger", {}, { headers: adminHeaders() });
			if (response.data?.status === "already_running") {
				toast.info("A descoberta automática já está em execução.");
			} else {
				toast.success("Descoberta de polémicas iniciada em background!");
			}
			setDiscoverRefresh((n) => n + 1);
		} catch (e) {
			toast.error("Erro ao iniciar descoberta automática");
		} finally {
			setTriggeringDiscover(false);
		}
	};

	const saveSettings = async () => {
		setSavingSettings(true);
		try {
			const res = await api.post("/admin/app-settings", appSettings, { headers: adminHeaders() });
			setAppSettings(res.data.settings);
			toast.success("Configurações de automação guardadas!");
		} catch (e) {
			toast.error("Erro ao guardar automações");
		} finally {
			setSavingSettings(false);
		}
	};

	const ingest = async () => {
                setIngesting(true);
                try {
                        const r = await api.post(
                                "/admin/ingest",
                                {},
                                { headers: adminHeaders() }
                        );
                        const falhadas = r.data.failed_outlets || [];
                        if (falhadas.length > 0) {
                                toast.error(
                                        `${r.data.total} novos artigos. Fontes em falha: ${falhadas.join(", ")}`,
                                        { duration: 8000 }
                                );
                        } else if (r.data.total === 0) {
                                // Distinguir "já estava tudo sincronizado" de "avariou":
                                // sem isto, um zero legítimo lê-se como falha.
                                toast.success("Sem artigos novos — as fontes já estavam sincronizadas");
                        } else {
                                toast.success(`${r.data.total} novos artigos indexados`);
                        }
                        reload();
                } catch (e) {
                        toast.error("Erro na ingestão RSS");
                } finally {
                        setIngesting(false);
                }
        };

        const saveLLMSettings = async () => {
                if (!model.trim()) return;
                setSavingLLM(true);
                try {
                        const response = await api.put(
                                "/admin/llm-settings",
                                {
                                        provider,
                                        model: model.trim(),
                                        model_write: roleModels.write?.trim() || null,
                                        model_reason: roleModels.reason?.trim() || null,
                                        model_summarize: roleModels.summarize?.trim() || null,
                                },
                                { headers: adminHeaders() }
                        );
                        if (response.data.role_models) setRoleModels(response.data.role_models);
                        toast.success(
                                response.data.configured
                                        ? "Configuração de IA guardada."
                                        : "Configuração guardada, mas falta a chave deste fornecedor no .env."
                        );
                } catch (e) {
                        toast.error(e.response?.data?.detail || "Não foi possível guardar a configuração de IA.");
                } finally {
                        setSavingLLM(false);
                }
        };

        const save = async () => {
                if (form.compact) {
                        toast.info("Aguarda pelo carregamento completo do dossiê antes de guardar.");
                        return;
                }
                if (!form.title.trim() || !form.summary.trim()) {
                        toast.error("Título e resumo são obrigatórios.");
                        return;
                }
                if (
                        form.editorial_status === "published" &&
                        form.relevance_scope === "fora_de_escopo"
                ) {
                        toast.error("Um dossiê fora de escopo não pode ser publicado.");
                        return;
                }
                if (
                        form.editorial_status === "published" &&
                        ["externo_relevante", "manual_editorial"].includes(form.relevance_scope) &&
                        !form.portugal_relevance.trim()
                ) {
                        toast.error("Explica a relevância para Portugal antes de publicar.");
                        return;
                }
                setSaving(true);
                try {
                        let savedTopic = null;
                        if (editing === "new") {
                                const response = await api.post("/admin/topics", { ...form, research_evidence: researchEvidence }, { headers: adminHeaders() });
                                savedTopic = response.data;
                                toast.success("Dossiê criado.");
                        } else {
                                const response = await api.put(`/admin/topics/${editing.id}`, { ...form, research_evidence: researchEvidence }, {
                                        headers: adminHeaders(),
                                });
                                savedTopic = response.data;
                                toast.success("Dossiê atualizado.");
                        }
                        if (savedTopic?.id && contextRiskReview) {
                                await persistContextRiskReviewState(savedTopic.id, contextRiskReview);
                        }
                        if (notifySubscribers) {
                                if (savedTopic?.editorial_status !== "published") {
                                        toast.info("Dossiê guardado sem notificar porque não está publicado.");
                                } else {
                                        try {
                                                const notification = await api.post(
                                                        `/admin/topics/${savedTopic.id}/notify`,
                                                        {},
                                                        { headers: adminHeaders() }
                                                );
                                                const data = notification.data || {};
                                                if (data.status === "already_recorded") {
                                                        toast.info("Este dossiê já tinha uma tentativa de notificação registada.");
                                                } else if (data.status === "no_subscribers") {
                                                        toast.info("Dossiê publicado, mas ainda não há subscritores activos.");
                                                } else if (data.failed_count > 0) {
                                                        toast.warning(
                                                                `Notificação enviada a ${data.sent_count || 0}; falharam ${data.failed_count}.`
                                                        );
                                                } else {
                                                        toast.success(`Notificação enviada a ${data.sent_count || 0} subscritores.`);
                                                }
                                        } catch (error) {
                                                toast.error(apiErrorMessage(error, "Dossiê guardado, mas a notificação falhou."));
                                        }
                                }
                        }
                        cancel();
                        reload();
                } catch (e) {
                        toast.error(e.response?.data?.detail || "Erro ao guardar");
                } finally {
                        setSaving(false);
                }
        };

        const applyContextRiskSuggestion = (excerpts, suggestion) => {
                const fields = ["full_article", "ai_analysis", "deception_strategy", "summary", "title"];
                const candidates = [...new Set(
                        (excerpts || [])
                                .map((excerpt) =>
                                        String(excerpt || "")
                                                .trim()
                                                .replace(/^[“”"']+|[“”"']+$/g, "")
                                                .trim()
                                )
                                .filter(Boolean)
                )];

                for (const field of fields) {
                        const value = String(form[field] || "");
                        const match = candidates.find((candidate) => value.includes(candidate));
                        if (match) {
                                setForm((current) => ({
                                        ...current,
                                        [field]: String(current[field] || "").replace(match, suggestion),
                                }));
                                return true;
                        }
                }

                return false;
        };

        const locateContextRiskExcerpt = (excerpt) => {
                const candidate = String(excerpt || "")
                        .trim()
                        .replace(/^[“”"']+|[“”"']+$/g, "")
                        .trim();
                if (!candidate) {
                        toast.info("Não há excerto para localizar.");
                        return false;
                }

                const fields = ["full_article", "ai_analysis", "deception_strategy", "summary", "title"];
                for (const field of fields) {
                        const value = String(form[field] || "");
                        const start = value.indexOf(candidate);
                        if (start < 0) continue;

                        const targetSection =
                                field === "full_article" || field === "ai_analysis" || field === "deception_strategy"
                                        ? "editor-section-text"
                                        : "editor-section-base";
                        // Troca para a aba certa antes de localizar o excerto.
                        setEditorSection(targetSection);
                        window.setTimeout(() => {
                                document.getElementById(targetSection)?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                });
                        }, 40);

                        window.setTimeout(() => {
                                const element = editorFieldRefs.current[field];
                                element?.focus();
                                if (typeof element?.setSelectionRange === "function") {
                                        element.setSelectionRange(start, start + candidate.length);
                                }
                        }, 260);
                        toast.success("Excerto localizado no editor.");
                        return true;
                }

                toast.info("Não encontrei esse excerto exacto nos campos editáveis.");
                return false;
        };

        const remove = async (id) => {
                if (!window.confirm("Eliminar este dossiê em definitivo?")) return;
                try {
                        await api.delete(`/admin/topics/${id}`, { headers: adminHeaders() });
                        toast.success("Dossiê eliminado.");
                        reload();
                } catch {
                        toast.error("Erro ao eliminar");
                }
        };

        const toggleFeatured = async (t) => {
                try {
                        await api.put(
                                `/admin/topics/${t.id}`,
                                { is_featured: !t.is_featured },
                                { headers: adminHeaders() }
                        );
                        toast.success(t.is_featured ? "Destaque removido." : "Destaque ativado.");
                        reload();
                } catch {
                        toast.error("Erro ao alterar destaque");
                }
        };

        const logout = async () => {
                try {
                        await api.post("/admin/logout");
                } finally {
                        navigate("/admin");
                }
        };


        const rejectSuggestion = async (id) => {
                if (!window.confirm("Ignorar esta sugestão?")) return;
                try {
                        await api.delete(`/admin/suggestions/${id}`, { headers: adminHeaders() });
                        toast.success("Sugestão rejeitada.");
                        reload();
                } catch {
                        toast.error("Erro ao rejeitar");
                }
        };

        const editorialCounts = EDITORIAL_STATUSES.reduce((acc, status) => {
                acc[status.value] = topics.filter((topic) => (topic.editorial_status || "published") === status.value).length;
                return acc;
        }, {});
        const visibleTopics = topicStatusFilter === "all"
                ? topics
                : topics.filter((topic) => (topic.editorial_status || "published") === topicStatusFilter);
        const pendingTipsCount = tips.filter((tip) => tip.status === "pending").length;
        const tabBadges = {
                inbox: suggestions.length + pendingTipsCount,
                queue: visibleTopics.length,
                risk: riskFindings.length || "",
                editor: editing ? "ativo" : "",
        };
        const hasLowerWorkspace = ["inbox", "queue", "risk", "editor"].includes(activeTab);

        return (
                <div className="admin-ui min-h-screen bg-[var(--bg)] text-[var(--ink)]">
                        <aside
                                className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-[var(--line)] bg-[var(--paper)] transition-transform duration-200 ${
                                        sidebarOpen ? "translate-x-0" : "-translate-x-full"
                                } lg:translate-x-0`}
                        >
                                <div className="flex h-16 items-center gap-3 border-b border-[var(--line)] px-5">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--controversy)] text-sm font-bold text-white">
                                                VO
                                        </div>
                                        <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold leading-tight text-[var(--ink)]">
                                                        Verdade Oculta
                                                </div>
                                                <div className="text-[11px] leading-tight text-[var(--ink-faint)]">
                                                        Consola editorial
                                                </div>
                                        </div>
                                </div>
                                <nav className="flex-1 overflow-y-auto px-3 py-4">
                                        <div className="overline mb-2 px-3 text-[var(--ink-faint)]">Painel</div>
                                        <div
                                                role="tablist"
                                                aria-label="Áreas do painel de administração"
                                                aria-orientation="vertical"
                                                className="flex flex-col gap-1"
                                        >
                                                {ADMIN_TABS.map((tab) => {
                                                        const Icon = tab.icon;
                                                        const active = activeTab === tab.key;
                                                        const badge = tabBadges[tab.key];
                                                        return (
                                                                <Fragment key={tab.key}>
                                                                        {tab.key === "settings" && (
                                                                                <div className="overline mb-2 mt-5 px-3 text-[var(--ink-faint)]">
                                                                                        Sistema
                                                                                </div>
                                                                        )}
                                                                        <button
                                                                                type="button"
                                                                                role="tab"
                                                                                data-testid={`admin-tab-${tab.key}`}
                                                                                aria-selected={active}
                                                                                onClick={() => {
                                                                                        setActiveTab(tab.key);
                                                                                        setSidebarOpen(false);
                                                                                }}
                                                                                className="adm-nav-item"
                                                                        >
                                                                                <Icon size={16} strokeWidth={1.75} />
                                                                                <span className="flex-1 truncate text-left">{tab.label}</span>
                                                                                {badge ? <span className="adm-badge">{badge}</span> : null}
                                                                        </button>
                                                                </Fragment>
                                                        );
                                                })}
                                        </div>
                                </nav>
                                <div className="space-y-1 border-t border-[var(--line)] p-3">
                                        <Link
                                                to="/"
                                                className="adm-btn adm-btn-ghost w-full justify-start"
                                        >
                                                <Globe size={16} strokeWidth={1.75} /> Ver site público
                                        </Link>
                                        <button
                                                data-testid={ADMIN.logoutBtn}
                                                type="button"
                                                onClick={logout}
                                                className="adm-btn adm-btn-ghost w-full justify-start"
                                        >
                                                <LogOut size={16} strokeWidth={1.75} /> Sair
                                        </button>
                                </div>
                        </aside>
                        {sidebarOpen && (
                                <button
                                        type="button"
                                        aria-label="Fechar menu"
                                        onClick={() => setSidebarOpen(false)}
                                        className="fixed inset-0 z-30 bg-black/30 lg:hidden"
                                />
                        )}
                        <div className="flex min-h-screen flex-col lg:pl-[248px]">
                                <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-[var(--line)] bg-[var(--paper)] px-4 md:px-8">
                                        <button
                                                type="button"
                                                aria-label="Abrir menu"
                                                onClick={() => setSidebarOpen(true)}
                                                className="adm-btn adm-btn-ghost px-2 lg:hidden"
                                        >
                                                <Menu size={18} strokeWidth={1.75} />
                                        </button>
                                        <h1 className="truncate text-[15px] font-semibold text-[var(--ink)]">
                                                {ADMIN_TABS.find((tab) => tab.key === activeTab)?.label}
                                        </h1>
                                        <div className="ml-auto flex items-center gap-2">
                                                <button
                                                        type="button"
                                                        onClick={triggerAutoDiscover}
                                                        disabled={triggeringDiscover}
                                                        className="adm-btn adm-btn-outline"
                                                        title="Descobrir Polémicas Agora"
                                                >
                                                        {triggeringDiscover ? (
                                                                <Loader2 size={15} strokeWidth={1.75} className="animate-spin" />
                                                        ) : (
                                                                <Download size={15} strokeWidth={1.75} />
                                                        )}
                                                        <span className="hidden md:inline">Descobrir Polémicas</span>
                                                </button>
                                                <button
                                                        data-testid="admin-ingest-btn"
                                                        type="button"
                                                        onClick={ingest}
                                                        disabled={ingesting}
                                                        className="adm-btn adm-btn-outline"
                                                        title="Sincronizar RSS"
                                                >
                                                        {ingesting ? (
                                                                <Loader2 size={15} strokeWidth={1.75} className="animate-spin" />
                                                        ) : (
                                                                <Download size={15} strokeWidth={1.75} />
                                                        )}
                                                        <span className="hidden md:inline">Sincronizar RSS</span>
                                                </button>
                                                <button
                                                        data-testid={ADMIN.newTopicBtn}
                                                        type="button"
                                                        onClick={startNew}
                                                        className="adm-btn adm-btn-primary"
                                                >
                                                        <Plus size={15} strokeWidth={1.75} />
                                                        <span className="hidden sm:inline">Novo dossiê</span>
                                                </button>
                                        </div>
                                </header>
                                <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-8 md:px-8">
                        <AutoDiscoverMonitor refreshKey={discoverRefresh} onFinished={reload} />
                        <VerifiedJobBanner job={verifiedJob} eta={verifiedQuota?.eta} onOpen={openVerifiedResult} onDiscard={() => setVerifiedJob(null)}
                                onRetry={verifiedJob?.request ? () => {
                                        const { body, meta } = verifiedJob.request;
                                        setVerifiedJob(null);
                                        startVerified(body, meta || {});
                                } : undefined}
                        />
                        <VerifiedQuotaStatus quota={verifiedQuota} />
                        {activeTab === "settings" && (
                        <div className="space-y-6">
                                <section className="adm-card p-6">
                                        <div className="mb-4">
                                                <h2 className="text-base font-semibold text-[var(--ink)]">Inteligência Artificial</h2>
                                                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                                                        Escolha o fornecedor e os modelos usados na geração e análise. A escolha fica guardada na base de dados; as chaves permanecem apenas no ficheiro .env do backend.
                                                </p>
                                        </div>
                                        <LLMSelector
                                                value={provider}
                                                onChange={(nextProvider) => {
                                                        // Ao MUDAR para OpenCode Go, repõe os papéis com os defaults
                                                        // corretos (os valores anteriores pertenciam a outro fornecedor).
                                                        const switchingToOpencode =
                                                                nextProvider === "opencode" && provider !== "opencode";
                                                        setProvider(nextProvider);
                                                        const info = llmProviders.find((item) => item.provider === nextProvider);
                                                        if (info?.default_model) setModel(info.default_model);
                                                        if (switchingToOpencode) {
                                                                setRoleModels({ ...OPENCODE_ROLE_DEFAULTS });
                                                        }
                                                }}
                                                model={model}
                                                onModelChange={setModel}
                                                providers={llmProviders}
                                                showModel={provider !== "opencode"}
                                        />

                                        {provider === "opencode" && (
                                                <div className="mt-6 border-t border-[var(--line)] pt-6">
                                                        <div className="overline mb-1 text-[var(--ink-faint)]">Modelos por tarefa</div>
                                                        <p className="mb-4 text-sm text-[var(--ink-soft)]">
                                                                Atribui um modelo a cada tarefa. Em branco, usa o modelo por defeito indicado. O papel "Ler/resumir" fica configurado para uso na próxima ronda.
                                                        </p>
                                                        <datalist id="opencode-models">
                                                                {opencodeCatalog.map((m) => (
                                                                        <option key={m} value={m} />
                                                                ))}
                                                        </datalist>
                                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                                                {[
                                                                        ["write", "Escrever a peça final", "fluidez e tom humano"],
                                                                        ["reason", "Estruturar e checar factos", "argumentos e verificação"],
                                                                        ["summarize", "Ler / resumir fontes", "ronda 2"],
                                                                ].map(([role, label, note]) => (
                                                                        <div key={role}>
                                                                                <label className="overline mb-1 block text-[var(--ink-soft)]">{label}</label>
                                                                                <input
                                                                                        list="opencode-models"
                                                                                        value={roleModels[role] || ""}
                                                                                        onChange={(e) => setRoleModels((r) => ({ ...r, [role]: e.target.value }))}
                                                                                        placeholder={roleDefaults[role] || OPENCODE_ROLE_DEFAULTS[role] || model}
                                                                                        className="w-full border bg-transparent px-3 py-2 font-mono text-sm"
                                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                                />
                                                                                <p className="mt-1 text-[11px] text-[var(--ink-faint)]">{note}</p>
                                                                        </div>
                                                                ))}
                                                        </div>
                                                </div>
                                        )}

                                        <div className="mt-6">
                                                <button
                                                        type="button"
                                                        onClick={saveLLMSettings}
                                                        disabled={savingLLM || !model.trim()}
                                                        className="adm-btn adm-btn-primary"
                                                >
                                                        {savingLLM ? "A guardar..." : "Guardar configuração de IA"}
                                                </button>
                                        </div>
                                </section>
                                <section className="adm-card p-6">
                                        <div className="mb-4">
                                                <h2 className="text-base font-semibold text-[var(--ink)]">Automatização</h2>
                                                <p className="mt-1 text-sm text-[var(--ink-soft)]">
                                                        Controla a descoberta automática de polémicas e a sincronização de fontes RSS.
                                                </p>
                                        </div>
                                        <div className="flex flex-col gap-3 rounded-lg border border-[var(--line)] bg-[var(--paper-2)] p-4">
                                                <div className="overline text-[var(--ink-faint)]">Configurações de Automatização</div>
                                                <div className="flex gap-4 items-center">
                                                    <label className="text-xs uppercase flex items-center gap-2 text-[var(--ink)]">
                                                        <span>Descoberta IA</span>
                                                        <input type="checkbox" checked={appSettings.auto_discover_enabled} onChange={(e) => setAppSettings(s => ({...s, auto_discover_enabled: e.target.checked}))} className="accent-[var(--controversy)]" />
                                                    </label>
                                                    <label className="text-xs uppercase flex items-center gap-2 text-[var(--ink)]">
                                                        <span>Minutos:</span>
                                                        <input type="number" min="1" value={appSettings.auto_discover_interval_minutes} onChange={(e) => setAppSettings(s => ({...s, auto_discover_interval_minutes: e.target.value}))} className="border border-[var(--line-strong)] bg-transparent w-16 px-2 py-1 text-center" />
                                                    </label>
                                                    <label className="text-xs uppercase flex items-center gap-2 text-[var(--ink)]">
                                                        <span>Qtd:</span>
                                                        <input type="number" min="1" max="10" value={appSettings.auto_discover_limit} onChange={(e) => setAppSettings(s => ({...s, auto_discover_limit: e.target.value}))} className="border border-[var(--line-strong)] bg-transparent w-12 px-2 py-1 text-center" />
                                                    </label>
                                                </div>
                                                <div className="flex gap-4 items-center">
                                                    <label className="text-xs uppercase flex items-center gap-2 text-[var(--ink)]">
                                                        <span>Sincronização RSS</span>
                                                        <input type="checkbox" checked={appSettings.rss_sync_enabled} onChange={(e) => setAppSettings(s => ({...s, rss_sync_enabled: e.target.checked}))} className="accent-[var(--controversy)]" />
                                                    </label>
                                                    <label className="text-xs uppercase flex items-center gap-2 text-[var(--ink)]">
                                                        <span>Minutos:</span>
                                                        <input type="number" min="1" value={appSettings.rss_sync_interval_minutes} onChange={(e) => setAppSettings(s => ({...s, rss_sync_interval_minutes: e.target.value}))} className="border border-[var(--line-strong)] bg-transparent w-16 px-2 py-1 text-center" />
                                                    </label>
                                                </div>
                                                <button
                                                        onClick={saveSettings}
                                                        disabled={savingSettings}
                                                        className="adm-btn adm-btn-primary mt-2 self-start"
                                                >
                                                        {savingSettings ? "A Guardar..." : "Guardar Automações"}
                                                </button>
                                        </div>
                                </section>
                        </div>
                        )}

                        {/* Quick stats banner */}
                        {stats && activeTab === "operation" && (
                                <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                                        {[
                                                ["Dossiês", stats.topics_total, "var(--ink)"],
                                                ["Artigos indexados", stats.articles_total, "var(--ai)"],
                                                ["Índice médio", `${stats.avg_controversy}/100`, "var(--controversy)"],
                                                ["Fontes activas", stats.by_outlet?.length || 0, "var(--truth)"],
                                        ].map(([label, value, color]) => (
                                                <div key={label} className="adm-kpi">
                                                        <div className="overline text-[var(--ink-faint)] mb-2">
                                                                {label}
                                                        </div>
                                                        <div
                                                                className="text-2xl font-semibold tracking-tight"
                                                                style={{ color }}
                                                        >
                                                                {value}
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                        )}

                        {activeTab === "operation" && (
                                <AdminOperationsPanel
                                        auditLog={auditLog}
                                        cleaningUploads={cleaningUploads}
                                        loadingOps={loadingOps}
                                        onCleanupUploads={cleanupUnusedUploads}
                                        onRefresh={loadOperations}
                                        opsJobs={opsJobs}
                                        opsStatus={opsStatus}
                                        opsUsage={opsUsage}
                                        uploadCleanupResult={uploadCleanupResult}
                                />
                        )}

                        {/* Research-by-term */}
                        {activeTab === "generate" && (
                        <div className="adm-card p-6 mb-8 relative">
                                <div className="absolute -top-3 left-6 stamp-ai stamp">
                                        Pesquisar + Gerar
                                </div>
                                <div className="flex items-center gap-3 mb-4 mt-2">
                                        <Search
                                                size={16}
                                                strokeWidth={1.5}
                                                style={{ color: "var(--ai)" }}
                                        />
                                        <div className="overline" style={{ color: "var(--ai)" }}>
                                                Investigar tema ou briefing
                                        </div>
                                </div>
                                <p className="text-sm text-[var(--ink-soft)] mb-4">
                                        Escreve livremente o que queres investigar, em linguagem natural (até 7900 caracteres).
                                        A IA tira do teu texto o tema a pesquisar, cruza os {stats?.articles_total || 0} artigos
                                        indexados com a web, e o Infinitum escreve e verifica o dossiê a partir do teu texto completo. Se colares links de notícias ou declarações, entram diretamente como fontes.
                                </p>
                                <div className="flex flex-col gap-3">
                                        <textarea
                                                data-testid="admin-research-input"
                                                value={researchQuery}
                                                maxLength={7900}
                                                onChange={(e) => setResearchQuery(e.target.value)}
                                                placeholder="ex.: Quero investigar como certas manchetes sobre segurança pública induzem medo e que casos recentes sustentam esse enquadramento..."
                                                rows={5}
                                                className="border bg-transparent px-3 py-3 text-sm font-mono w-full resize-y min-h-[120px]"
                                                style={{ borderColor: "var(--line-strong)" }}
                                                onKeyDown={(e) => {
                                                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && researchQuery.trim().length >= 10) startVerified({ mode: "research", brief: researchQuery, category: researchCategory || undefined });
                                                }}
                                        />
                                        <label className="flex flex-col gap-1 text-xs text-[var(--ink-soft)] md:max-w-xs">
                                                Categoria
                                                <select
                                                        data-testid="admin-research-category"
                                                        value={researchCategory}
                                                        onChange={(e) => setResearchCategory(e.target.value)}
                                                        className="border bg-transparent px-3 py-2 text-sm"
                                                        style={{ borderColor: "var(--line-strong)" }}
                                                >
                                                        <option value="">Automático (o Infinitum deduz o tema)</option>
                                                        {CATEGORIES.map((c) => (
                                                                <option key={c.slug} value={c.slug}>{c.label}</option>
                                                        ))}
                                                </select>
                                        </label>
                                        <p className={`text-xs text-right ${researchQuery.length > 7500 ? "text-[var(--controversy)]" : "text-[var(--ink-faint)]"}`} data-testid="admin-research-counter">
                                                {researchQuery.length.toLocaleString("pt-PT")} / 7900 caracteres
                                        </p>
                                        <button
                                                data-testid="admin-research-btn"
                                                type="button"
                                                onClick={async () => {
                                                        const ok = await startVerified({ mode: "research", brief: researchQuery, category: researchCategory || undefined });
                                                        if (ok) setResearchQuery("");
                                                }}
                                                disabled={startingVerified || researchQuery.trim().length < 10}
                                                className="overline px-5 py-3 bg-[var(--ai)] text-[var(--bg-deep)] hover:bg-[var(--ink)] inline-flex items-center justify-center gap-2 disabled:opacity-50 md:self-start"
                                        >
                                                {startingVerified ? (
                                                        <>
                                                                <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> A lançar...
                                                        </>
                                                ) : (
                                                        <>
                                                                <FileText size={14} strokeWidth={1.5} /> Investigar (escrita verificada)
                                                        </>
                                                )}
                                        </button>
                                </div>
                        </div>
                        )}

                        <div className={`${hasLowerWorkspace ? "grid" : "hidden"} grid-cols-1 lg:grid-cols-12 gap-12`}>
                                {/* List */}
                                <aside className={`${activeTab === "inbox" ? "lg:col-span-8" : activeTab === "queue" ? "lg:col-span-12" : "hidden"} flex flex-col gap-8`}>
                                        {/* Sugestões do Público */}
                                        <div className={activeTab === "inbox" ? "" : "hidden"}>
                                                <div className="flex items-center gap-2 text-[var(--controversy)] mb-4">
                                                        <Search size={16} />
                                                        <span className="overline">Sugestões do Público ({suggestions.length})</span>
                                                </div>
                                                <ul className="adm-card overflow-hidden mb-8">
                                                        {suggestions.map((sug) => (
                                                                <li key={sug.id} className="border-b border-[var(--line)] last:border-b-0 p-4">
                                                                        <div className="flex flex-col gap-2">
                                                                                <p className="font-serif text-lg text-[var(--ink)] leading-tight">{sug.query}</p>
                                                                                <div className="flex items-center gap-2">
                                                                                        <span className="text-[10px] overline px-2 py-0.5 border border-[var(--line-strong)] text-[var(--ink-soft)] uppercase">{sug.category}</span>
                                                                                        <span className="text-[10px] font-mono text-[var(--ink-faint)]">{new Date(sug.created_at).toLocaleDateString("pt-PT")}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2 mt-2">
                                                                                        <button
                                                                                                onClick={() => startVerified({ mode: "suggestion", suggestion_id: sug.id, title: sug.query, category: sug.category || undefined })}
                                                                                                disabled={startingVerified}
                                                                                                className="overline px-3 py-1.5 border border-[var(--truth)] text-[var(--truth)] hover:bg-[var(--truth)] hover:text-[var(--bg-deep)] transition-colors text-[10px] flex items-center gap-1 disabled:opacity-50"
                                                                                        >
                                                                                                <CheckCircle2 size={12} /> Gerar verificado
                                                                                        </button>
                                                                                        <button onClick={() => rejectSuggestion(sug.id)} className="overline px-3 py-1.5 border border-[var(--line-strong)] text-[var(--ink-soft)] hover:text-[var(--deception)] hover:border-[var(--deception)] transition-colors text-[10px]">
                                                                                                Ignorar
                                                                                        </button>
                                                                                </div>
                                                                        </div>
                                                                </li>
                                                        ))}
                                                        {suggestions.length === 0 && (
                                                                <li className="p-4 text-xs text-[var(--ink-faint)] font-mono italic">
                                                                        Nenhuma sugestão nova.
                                                                </li>
                                                        )}
                                                </ul>
                                        </div>

                                        {/* Tips / Denúncias */}
                                        <div className={activeTab === "inbox" ? "" : "hidden"}>
                                                <div className="flex items-center gap-2 text-[var(--deception)] mb-4">
                                                        <AlertOctagon size={16} />
                                                        <span className="overline">Inbox de Denúncias ({pendingTipsCount})</span>
                                                </div>
                                                <ul className="adm-card overflow-hidden">
                                                        {tips.map((tip) => (
                                                                <li key={tip.id} className={`border-b border-[var(--line)] last:border-b-0 p-4 ${tip.status === "analyzed" ? "opacity-50 grayscale" : ""}`}>
                                                                        <div className="flex flex-col gap-2">
                                                                                <p className="font-mono text-xs text-[var(--ink)] line-clamp-3">"{tip.content}"</p>
                                                                                {tip.source_url && (
                                                                                        <a href={tip.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] overline text-[var(--ai)] hover:underline truncate">Ver Prova ({tip.source_url})</a>
                                                                                )}
                                                                                <div className="flex items-center justify-between mt-2">
                                                                                        <span className="text-[10px] font-mono text-[var(--ink-soft)]">{new Date(tip.created_at).toLocaleDateString("pt-PT")}</span>
                                                                                        {tip.status === "pending" ? (
                                                                                                <button onClick={() => analyzeTip(tip)} className="overline px-3 py-1.5 bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--controversy)] transition-colors text-[10px]">
                                                                                                        Analisar
                                                                                                </button>
                                                                                        ) : (
                                                                                                <span className="text-[10px] overline text-[var(--truth)]">Analisada</span>
                                                                                        )}
                                                                                </div>
                                                                        </div>
                                                                </li>
                                                        ))}
                                                        {tips.length === 0 && (
                                                                <li className="p-4 text-xs text-[var(--ink-faint)] font-mono italic">
                                                                        Nenhuma denúncia na caixa de entrada.
                                                                </li>
                                                        )}
                                                </ul>
                                        </div>

                                        <div className={activeTab === "queue" ? "" : "hidden"}>
                                                <EditorialQueue
                                                        editorialCounts={editorialCounts}
                                                        editing={editing}
                                                        loadingTopics={loadingTopics}
                                                        hasMoreTopics={topicPage.has_more}
                                                        onDelete={remove}
                                                        onEdit={startEdit}
                                                        onLoadMore={loadMoreAdminTopics}
                                                        onStatusFilterChange={setTopicStatusFilter}
                                                        onTopicSearchChange={setTopicSearch}
                                                        onTopicSearchClear={clearEditorialTopicSearch}
                                                        onTopicSearchSubmit={searchEditorialTopics}
                                                        onToggleFeatured={toggleFeatured}
                                                        statuses={EDITORIAL_STATUSES}
                                                        topicSearch={topicSearch}
                                                        totalTopics={topicPage.total || topics.length}
                                                        topicStatusFilter={topicStatusFilter}
                                                        topics={topics}
                                                        visibleTopics={visibleTopics}
                                                />
                                        </div>
                                </aside>

                                {activeTab === "risk" && (
                                        <section className="lg:col-span-12">
                                                <LegalRiskPanel
                                                        editorialStatus={riskEditorialStatus}
                                                        findings={riskFindings}
                                                        limit={riskScanLimit}
                                                        model={model}
                                                        onEdit={startEdit}
                                                        provider={provider}
                                                        setEditorialStatus={setRiskEditorialStatus}
                                                        setFindings={setRiskFindings}
                                                        setLimit={setRiskScanLimit}
                                                        topics={topics}
                                                />
                                        </section>
                                )}

                                {/* Editor */}
                                <section className={activeTab === "editor" ? "lg:col-span-12" : "hidden"}>
                                        {!editing && (
                                                <div className="adm-card p-16 text-center">
                                                        <p className="font-serif text-3xl mb-2 text-[var(--ink)]">
                                                                Selecciona um dossiê.
                                                        </p>
                                                        <p className="text-[var(--ink-soft)] font-mono text-sm">
                                                                Ou cria um novo, ou investiga um termo acima.
                                                        </p>
                                                </div>
                                        )}

                                        {editing && (
                                                <div className="space-y-6">
                                                        <div className="adm-card flex flex-wrap items-center gap-3 p-3">
                                                                <span className="adm-badge">{editorialStatusLabel(form.editorial_status) || "Rascunho"}</span>
                                                                <span className="hidden max-w-[40ch] truncate text-sm text-[var(--ink-soft)] sm:inline">{form.title || "Sem título"}</span>
                                                                <div className="ml-auto flex flex-wrap items-center gap-2">
                                                                        <button type="button" onClick={() => setShowGenerator((v) => !v)} className="adm-btn adm-btn-outline">
                                                                                <Sparkles size={15} strokeWidth={1.75} /> Renovar IA
                                                                        </button>
                                                                        <button type="button" onClick={() => setEditorSection("editor-section-review")} className="adm-btn adm-btn-outline">
                                                                                <AlertOctagon size={15} strokeWidth={1.75} /> Revisão
                                                                        </button>
                                                                        <button type="button" onClick={save} disabled={saving || form.compact} className="adm-btn adm-btn-primary">
                                                                                <CheckCircle2 size={15} strokeWidth={1.75} /> {saving ? "A guardar..." : "Guardar"}
                                                                        </button>
                                                                </div>
                                                        </div>

                                                        {(showGenerator || editing === "new") && (
                                                        <div className="adm-card p-6 relative">
                                                                        <div className="absolute -top-3 left-6 stamp-ai stamp">
                                                                                Gerador IA
                                                                        </div>
                                                                        <p className="mt-2 mb-2 text-xs text-[var(--ink-faint)]">
                                                                                Escrita: {provider}
                                                                                {roleModels.write ? ` · ${roleModels.write}` : model ? ` · ${model}` : ""} — alterável em Configurações.
                                                                        </p>
                                                                        <textarea
                                                                                data-testid={ADMIN.hintInput}
                                                                                value={hint}
                                                                                onChange={(e) => setHint(e.target.value)}
                                                                                placeholder="Contexto opcional (ângulo, fontes a considerar)..."
                                                                                rows={3}
                                                                                className="w-full border bg-transparent p-3 text-sm mt-2 font-mono"
                                                                                style={{ borderColor: "var(--line)" }}
                                                                        />
                                                                        <button
                                                                                data-testid={ADMIN.generateBtn}
                                                                                type="button"
                                                                                onClick={() =>
                                                                                        editing === "new"
                                                                                                ? startVerified({ mode: "new", title: form.title, brief: hint, category: form.category, extra_sources: deepResearch?.verified_sources || [] })
                                                                                                : startVerified({ mode: "renew", existing_article: form, brief: hint, category: form.category, extra_sources: deepResearch?.verified_sources || [], ...(contextRiskReview ? { context_risk_review: { ...contextRiskReview } } : {}) }, { topicId: editing?.id })
                                                                                }
                                                                                disabled={startingVerified || !form.title.trim()}
                                                                                className="mt-4 overline px-5 py-3 bg-[var(--controversy)] text-[var(--bg-deep)] inline-flex items-center gap-2 disabled:opacity-50"
                                                                        >
                                                                                <CheckCircle2 size={14} strokeWidth={1.5} />
                                                                                {editing === "new" ? "Gerar dossiê verificado" : "Renovar com o Infinitum (verificado)"}
                                                                        </button>
                                                                        <p className="mt-2 text-xs text-[var(--ink-soft)] max-w-lg">
                                                                                O Infinitum lê as fontes, fixa os factos provados, escreve só com eles e faz a revisão jurídica (10–12 min). Se a quota estiver esgotada, o aviso diz quando se pode voltar a tentar.
                                                                        </p>
                                                                        {deepResearch && (
                                                                                <p className="mt-2 text-xs text-[var(--truth)]">
                                                                                        A escrita verificada vai receber {deepResearch.verified_sources?.length || 0} fonte(s) da pesquisa profunda.
                                                                                </p>
                                                                        )}
                                                                        
                                                                        <DeepResearchPanel
                                                                                form={form}
                                                                                topicKey={editing && editing !== "new" ? editing.id : form.id || "new"}
                                                                                onResult={setDeepResearch}
                                                                                onAddSources={addDeepResearchSources}
                                                                        />
                                                        </div>
                                                        )}

                                                        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
                                                        <div className="space-y-6">
                                                        <EditorWorkspaceNav
                                                                sections={EDITOR_WORKSPACE_SECTIONS}
                                                                active={editorSection}
                                                                onSelect={setEditorSection}
                                                                badges={{ "editor-section-review": activeWarningDetails(form.quality_warnings, { contextRiskReview }).length || "" }}
                                                        />

                                                        <EditorSection
                                                                active={editorSection === "editor-section-base"}
                                                                id="editor-section-base"
                                                                eyebrow="Editor · Base"
                                                                title="Identidade do dossiê"
                                                                description="Define o título, resumo, imagem, categoria, estado editorial e sinais de arquivo antes de trabalhar o corpo."
                                                        >
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                <div className="md:col-span-2">
                                                                        <div className="flex items-center justify-between mb-2">
                                                                                <label className="overline block text-[var(--ink-soft)]">Título</label>
                                                                                {verifiedJobId ? (
                                                                                        <div className="flex items-center gap-2">
                                                                                                <button
                                                                                                        type="button"
                                                                                                        onClick={() => runVerifiedTitle("validate")}
                                                                                                        disabled={Boolean(titleCheck.loading)}
                                                                                                        className="text-xs flex items-center gap-1 px-2 py-1 bg-[var(--paper-2)] hover:bg-[var(--line)] text-[var(--ink)] border border-[var(--line)] disabled:opacity-50"
                                                                                                >
                                                                                                        <CheckCircle2 size={12} />
                                                                                                        {titleCheck.loading === "validate" ? "A validar..." : "Validar título"}
                                                                                                </button>
                                                                                                <button
                                                                                                        type="button"
                                                                                                        onClick={() => runVerifiedTitle("suggest")}
                                                                                                        disabled={Boolean(titleCheck.loading)}
                                                                                                        className="text-xs flex items-center gap-1 px-2 py-1 bg-[var(--paper-2)] hover:bg-[var(--line)] text-[var(--ink)] border border-[var(--line)] disabled:opacity-50"
                                                                                                >
                                                                                                        <Sparkles size={12} />
                                                                                                        {titleCheck.loading === "suggest" ? "A gerar (1–3 min)..." : "Gerar título"}
                                                                                                </button>
                                                                                        </div>
                                                                                ) : (
                                                                                        <span className="text-[11px] text-[var(--ink-faint)]">
                                                                                                Títulos verificados: renova o dossiê com o Infinitum
                                                                                        </span>
                                                                                )}
                                                                        </div>
                                                                        <input
                                                                                ref={(node) => { editorFieldRefs.current.title = node; }}
                                                                                data-testid={ADMIN.titleInput}
                                                                                value={form.title}
                                                                                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                                                                className="w-full border bg-transparent px-4 py-3 font-serif text-xl"
                                                                                style={{ borderColor: "var(--line-strong)" }}
                                                                        />
                                                                        {verifiedJobId && (
                                                                                <p className={`mt-2 text-xs ${titlePendingValidation ? "text-[var(--ai)]" : "text-[var(--truth)]"}`} data-testid="title-validation-status">
                                                                                        {titlePendingValidation
                                                                                                ? "Título alterado à mão: ainda não validado pelo Infinitum (o artigo continua verificado)."
                                                                                                : "Título verificado pelo Infinitum contra os factos do dossiê."}
                                                                                </p>
                                                                        )}
                                                                        {titleCheck.result && (
                                                                                <div className="mt-3 border border-[var(--line)] bg-[var(--bg-deep)] p-3 text-sm space-y-2" data-testid="title-check-result">
                                                                                        {titleCheck.result.validation && !titleCheck.result.validation.ok && (
                                                                                                <div>
                                                                                                        <p className="text-[var(--deception)]">O título não passou:</p>
                                                                                                        <ul className="list-disc pl-5 text-xs text-[var(--ink-soft)]">
                                                                                                                {(titleCheck.result.validation.reasons || []).map((reason) => (
                                                                                                                        <li key={reason}>{reason}</li>
                                                                                                                ))}
                                                                                                        </ul>
                                                                                                </div>
                                                                                        )}
                                                                                        {(titleCheck.result.titles || []).length > 0 && (
                                                                                                <div>
                                                                                                        <p className="overline text-[var(--truth)] mb-1">Títulos verificados</p>
                                                                                                        <ul className="space-y-1">
                                                                                                                {titleCheck.result.titles.map((item) => (
                                                                                                                        <li key={item.title} className="flex items-start justify-between gap-3">
                                                                                                                                <span className="font-serif">{item.title}</span>
                                                                                                                                <button
                                                                                                                                        type="button"
                                                                                                                                        onClick={() => { markTitleValidated(item.title); setTitleCheck({ loading: null, result: null }); }}
                                                                                                                                        className="text-xs shrink-0 px-2 py-1 border border-[var(--truth)] text-[var(--truth)]"
                                                                                                                                >
                                                                                                                                        Usar
                                                                                                                                </button>
                                                                                                                        </li>
                                                                                                                ))}
                                                                                                        </ul>
                                                                                                </div>
                                                                                        )}
                                                                                        {titleCheck.result.mode === "suggest" && !(titleCheck.result.titles || []).length && (
                                                                                                <p className="text-xs text-[var(--ink-soft)]">Nenhum título passou a verificação nesta ronda.</p>
                                                                                        )}
                                                                                </div>
                                                                        )}
                                                                </div>
                                                                <div>
                                                                        <label className="overline block mb-2 text-[var(--ink-soft)]">Categoria</label>
                                                                        <select
                                                                                data-testid={ADMIN.categorySelect}
                                                                                value={form.category}
                                                                                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                                                                                className="w-full border bg-transparent px-4 py-3 font-mono text-sm"
                                                                                style={{ borderColor: "var(--line-strong)" }}
                                                                        >
                                                                                {CATEGORIES.map((c) => (
                                                                                        <option key={c.slug} value={c.slug} style={{ background: "var(--paper-2)" }}>
                                                                                                {c.label}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                <div>
                                                                        <label className="overline block mb-2 text-[var(--ink-soft)]">Estado editorial</label>
                                                                        <select
                                                                                value={form.editorial_status}
                                                                                onChange={(e) => setForm((f) => ({ ...f, editorial_status: e.target.value }))}
                                                                                className="w-full border bg-transparent px-4 py-3 font-mono text-sm"
                                                                                style={{ borderColor: "var(--line-strong)" }}
                                                                        >
                                                                                {EDITORIAL_STATUSES.map((status) => (
                                                                                        <option key={status.value} value={status.value} style={{ background: "var(--paper-2)" }}>
                                                                                                {status.label}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>
                                                                <div>
                                                                        <label className="overline block mb-2 text-[var(--ink-soft)]">Relevância</label>
                                                                        <select
                                                                                value={form.relevance_scope}
                                                                                onChange={(e) => setForm((f) => ({ ...f, relevance_scope: e.target.value }))}
                                                                                className="w-full border bg-transparent px-4 py-3 font-mono text-sm"
                                                                                style={{ borderColor: "var(--line-strong)" }}
                                                                        >
                                                                                {RELEVANCE_SCOPES.map((scope) => (
                                                                                        <option key={scope.value} value={scope.value} style={{ background: "var(--paper-2)" }}>
                                                                                                {scope.label}
                                                                                        </option>
                                                                                ))}
                                                                        </select>
                                                                </div>
                                                                <div className="md:col-span-1">
                                                                        <label className="overline block mb-2 text-[var(--ink-soft)]">Ligação a Portugal</label>
                                                                        <textarea
                                                                                value={form.portugal_relevance || ""}
                                                                                onChange={(e) => setForm((f) => ({ ...f, portugal_relevance: e.target.value }))}
                                                                                placeholder="Obrigatório para externos ou decisão editorial manual publicados."
                                                                                rows={3}
                                                                                className="w-full border bg-transparent px-4 py-3 text-sm"
                                                                                style={{ borderColor: "var(--line-strong)" }}
                                                                        />
                                                                </div>
                                                        </div>

                                                        <div>
                                                                <label className="overline block mb-2 text-[var(--ink-soft)]">Resumo</label>
                                                                <textarea
                                                                        ref={(node) => { editorFieldRefs.current.summary = node; }}
                                                                        data-testid="admin-summary-input"
                                                                        rows={3}
                                                                        value={form.summary}
                                                                        onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                                                                        className="w-full border bg-transparent p-3 text-base font-serif"
                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                />
                                                        </div>

                                                        <div>
                                                                <div className="flex gap-2 items-center mb-2">
                                                                        <label className="overline text-[var(--ink-soft)]">Imagem (URL)</label>
                                                                        <button
                                                                                type="button"
                                                                                onClick={() => { setShowImageModal(true); if (form.title && !(form.image_suggestions || []).length) searchMoreImages(); }}
                                                                                disabled={!form.title}
                                                                                className="adm-btn adm-btn-outline ml-auto"
                                                                        >
                                                                                <ImageIcon size={14} strokeWidth={1.75} /> Procurar imagem
                                                                        </button>
                                                                </div>
                                                                <input
                                                                        type="text"
                                                                        placeholder="https://exemplo.com/foto.jpg"
                                                                        value={form.image_url || ""}
                                                                        onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                                                                        className="w-full border bg-transparent p-3 text-sm font-mono mb-2"
                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                />
                                                                {form.image_url ? (
                                                                        <img
                                                                                src={`${API}/proxy-image?url=${encodeURIComponent(form.image_url)}`}
                                                                                alt="Imagem selecionada"
                                                                                className="mt-2 h-28 w-full rounded-lg border border-[var(--line)] object-cover"
                                                                        />
                                                                ) : null}
                                                                <Modal
                                                                        open={showImageModal}
                                                                        onClose={() => setShowImageModal(false)}
                                                                        title="Escolher imagem"
                                                                        maxWidth="max-w-2xl"
                                                                >
                                                                        <div className="mb-3 flex items-center gap-2">
                                                                                <button type="button" onClick={searchMoreImages} disabled={searchingImages || !form.title} className="adm-btn adm-btn-outline">
                                                                                        {searchingImages ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={1.75} />}
                                                                                        {searchingImages ? "A procurar..." : "Procurar mais"}
                                                                                </button>
                                                                                <span className="text-xs text-[var(--ink-faint)]">Clica numa imagem para a usar.</span>
                                                                        </div>
                                                                        {(form.image_suggestions || []).length > 0 ? (
                                                                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                                                                        {form.image_suggestions.map((imgUrl, i) => (
                                                                                                <button
                                                                                                        key={i}
                                                                                                        type="button"
                                                                                                        onClick={() => { setForm((f) => ({ ...f, image_url: imgUrl })); setShowImageModal(false); }}
                                                                                                        className={`relative h-28 overflow-hidden rounded-lg border-2 transition-all ${form.image_url === imgUrl ? "border-[var(--controversy)]" : "border-[var(--line)] hover:border-[var(--controversy)]"}`}
                                                                                                >
                                                                                                        <img src={`${API}/proxy-image?url=${encodeURIComponent(imgUrl)}`} alt={`Sugestão ${i + 1}`} className="h-full w-full object-cover" />
                                                                                                </button>
                                                                                        ))}
                                                                                </div>
                                                                        ) : (
                                                                                <p className="rounded-lg border border-dashed border-[var(--line)] p-6 text-center text-sm text-[var(--ink-faint)]">
                                                                                        {searchingImages ? "A procurar imagens..." : "Sem sugestões ainda. Carrega em \"Procurar mais\"."}
                                                                                </p>
                                                                        )}
                                                                </Modal>
                                                        </div>

                                                        <div>
                                                                <label className="overline block mb-2 text-[var(--ink-soft)]">
                                                                        Índice de controvérsia: {form.controversy_score}/100
                                                                </label>
                                                                <input
                                                                        type="range"
                                                                        min={0}
                                                                        max={100}
                                                                        value={form.controversy_score}
                                                                        onChange={(e) => setForm((f) => ({ ...f, controversy_score: Number(e.target.value) }))}
                                                                        className="w-full accent-[var(--controversy)]"
                                                                />
                                                        </div>

                                                        <StringList
                                                                label="Tags"
                                                                items={form.tags}
                                                                onChange={(v) => setForm((f) => ({ ...f, tags: v }))}
                                                                testId="admin-tag-input"
                                                                placeholder="ex: corrupção"
                                                        />
                                                        </EditorSection>

                                                        <EditorSection
                                                                active={editorSection === "editor-section-evidence"}
                                                                id="editor-section-evidence"
                                                                eyebrow="Editor · Evidência"
                                                                title="Factos, narrativas e fontes"
                                                                description="A matéria factual, o enquadramento a contrastar e as fontes que sustentam o dossiê."
                                                        >
                                                        <FactList
                                                                items={form.verified_facts}
                                                                onChange={(v) => setForm((f) => ({ ...f, verified_facts: v }))}
                                                        />

                                                        <StringList
                                                                label="Narrativas em circulação"
                                                                items={form.media_narratives}
                                                                onChange={(v) => setForm((f) => ({ ...f, media_narratives: v }))}
                                                                testId="admin-narrative-input"
                                                                placeholder="ex: enquadramento como..."
                                                        />

                                                        <SourceList
                                                                items={form.sources}
                                                                onChange={(v) => setForm((f) => ({ ...f, sources: v }))}
                                                        />
                                                        </EditorSection>

                                                        <EditorSection
                                                                active={editorSection === "editor-section-text"}
                                                                id="editor-section-text"
                                                                eyebrow="Editor · Texto"
                                                                title="Análise e artigo"
                                                                description="Trabalha a tese editorial, a análise cruzada e a peça completa com citações inline."
                                                        >
                                                        <div>
                                                                <label className="overline block mb-2 text-[var(--ink-soft)]">Estratégia de manipulação</label>
                                                                <textarea
                                                                        ref={(node) => { editorFieldRefs.current.deception_strategy = node; }}
                                                                        rows={5}
                                                                        value={form.deception_strategy}
                                                                        onChange={(e) => setForm((f) => ({ ...f, deception_strategy: e.target.value }))}
                                                                        className="w-full border bg-transparent p-3 text-base"
                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                />
                                                        </div>

                                                        <div>
                                                                <label className="overline block mb-2 text-[var(--ink-soft)]">Análise (IA / editorial)</label>
                                                                <textarea
                                                                        ref={(node) => { editorFieldRefs.current.ai_analysis = node; }}
                                                                        rows={10}
                                                                        value={form.ai_analysis}
                                                                        onChange={(e) => setForm((f) => ({ ...f, ai_analysis: e.target.value }))}
                                                                        className="w-full border bg-transparent p-3 text-base font-serif"
                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                />
                                                        </div>

                                                        <div>
                                                                <label className="overline block mb-2 text-[var(--ink-soft)]">Artigo investigativo completo</label>
                                                                <textarea
                                                                        ref={(node) => { editorFieldRefs.current.full_article = node; }}
                                                                        rows={18}
                                                                        value={form.full_article}
                                                                        onChange={(e) => setForm((f) => ({ ...f, full_article: e.target.value }))}
                                                                        className="w-full border bg-transparent p-3 text-base font-serif"
                                                                        style={{ borderColor: "var(--line-strong)" }}
                                                                        placeholder="Texto longo com referências [1], [2]..."
                                                                />
                                                        </div>
                                                        </EditorSection>

                                                        <EditorSection
                                                                active={editorSection === "editor-section-review"}
                                                                id="editor-section-review"
                                                                eyebrow="Editor · Revisão"
                                                                title="Risco e qualidade"
                                                                description="Confirma riscos reais no texto completo e revê a evidência extraída pela pesquisa."
                                                        >
                                                        <QualityWarningsPanel
                                                                contextRiskReview={contextRiskReview}
                                                                form={form}
                                                        />

                                                        <ContextualRiskReview
                                                                form={form}
                                                                model={model}
                                                                onAcceptSuggestion={applyContextRiskSuggestion}
                                                                onAddSource={addReviewedSourceToForm}
                                                                onLocateExcerpt={locateContextRiskExcerpt}
                                                                onRemoveSource={removeReviewedSourceFromForm}
                                                                onResolveWarnings={removeResolvedWarningsFromForm}
                                                                onReviewPersist={persistCurrentContextRiskReview}
                                                                onQualityWarnings={(warnings) => setForm((f) => ({ ...f, quality_warnings: warnings }))}
                                                                onEvidencePack={(pack) =>
                                                                        setForm((f) => ({ ...f, evidence_pack: pack || {} }))
                                                                }
                                                                provider={provider}
                                                                review={contextRiskReview}
                                                                researchEvidence={researchEvidence}
                                                                setReview={setContextRiskReview}
                                                        />

                                                        <ResearchEvidencePanel
                                                                evidence={researchEvidence}
                                                                reusedWebsets={reusedWebsets}
                                                                webset={researchWebset}
                                                        />

                                                        </EditorSection>

                                                        <EditorSection
                                                                active={editorSection === "editor-section-publish"}
                                                                id="editor-section-publish"
                                                                eyebrow="Editor · Guardar"
                                                                title="Decisão final"
                                                                description="Guardar actualiza o dossiê; publicar continua dependente do estado editorial escolhido acima."
                                                        >
                                                        <label className="flex flex-col gap-3 border border-[var(--line)] bg-[var(--paper)] p-4 sm:flex-row sm:items-center sm:justify-between">
                                                                <span className="flex items-start gap-3">
                                                                        <span className="mt-1 inline-flex h-9 w-9 items-center justify-center border border-[var(--line-strong)] text-[var(--ink-soft)]">
                                                                                <Bell size={16} strokeWidth={1.5} />
                                                                        </span>
                                                                        <span>
                                                                                <span className="overline block text-[var(--ink)]">
                                                                                        Notificar subscritores
                                                                                </span>
                                                                                <span className="block max-w-xl text-xs leading-relaxed text-[var(--ink-soft)]">
                                                                                        Envia uma notificação Web Push só se este dossiê for guardado como publicado. Fica desligado por defeito.
                                                                                </span>
                                                                        </span>
                                                                </span>
                                                                <input
                                                                        type="checkbox"
                                                                        checked={notifySubscribers}
                                                                        disabled={saving}
                                                                        onChange={(e) => setNotifySubscribers(e.target.checked)}
                                                                        className="h-5 w-5 shrink-0 accent-[var(--controversy)]"
                                                                        aria-label="Notificar subscritores ao guardar dossiê publicado"
                                                                />
                                                        </label>
                                                        <div className="flex items-center gap-3 pt-6 border-t border-[var(--line-strong)]">
                                                                <button
                                                                        data-testid={ADMIN.saveBtn}
                                                                        type="button"
                                                                        onClick={save}
                                                                        disabled={saving || form.compact}
                                                                        className="overline px-6 py-3 bg-[var(--controversy)] text-[var(--bg-deep)] hover:bg-[var(--ink)] disabled:opacity-50"
                                                                >
                                                                        {saving ? "A guardar..." : form.compact ? "A carregar..." : "Guardar"}
                                                                </button>
                                                                <button
                                                                        type="button"
                                                                        onClick={cancel}
                                                                        className="overline px-6 py-3 border border-[var(--line-strong)] hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] transition-colors"
                                                                >
                                                                        Cancelar
                                                                </button>
                                                        </div>
                                                        </EditorSection>
                                                        </div>
                                                        <EditorInspectorPanel
                                                                contextRiskReview={contextRiskReview}
                                                                form={form}
                                                                researchEvidence={researchEvidence}
                                                                reviewStateSavedAt={reviewStateSavedAt}
                                                        />
                                                </div>
                                                </div>
                                        )}
                                </section>
                        </div>
                                </main>
                        </div>
                </div>
        );
}
